import Ids from 'ids';
import {nodeToolRegistry} from '@antv/x6';
import ViewerX6 from './ViewerX6';
import connectNodeTool from './tools/connectNodeTool';
import Moddle from './moddle';
import PathMap from './draw/PathMap';
import {prepareForExport} from './export/XmlExportAdapter';
import {nodeTypeRegistry} from '@core_x6/lib/node-types';
import {buildModelFromJson} from './import/JsonLoader';
import {modelToJson} from './export/JsonSerializer';
import {
	ARCHIMATE_MODEL,
	ARCHIMATE_RELATIONSHIPS,
	RELATIONSHIP_ACCESS,
	RELATIONSHIP_ASSOCIATION,
	RELATIONSHIP_FLOW,
	RELATIONSHIP_SERVING,
	RELATIONSHIP_INFLUENCE,
	RELATIONSHIP_REALIZATION,
	RELATIONSHIP_AGGREGATION,
	RELATIONSHIP_COMPOSITION,
	RELATIONSHIP_ASSIGNMENT,
	RELATIONSHIP_SPECIALIZATION,
	RELATIONSHIP_TRIGGERING,
} from './metamodel/Concept';
import {
	TEMPLATE_MODEL,
	STENCIL_GROUPS,
	MIN_ELEMENT_WIDTH,
	getMinElementHeightForShape,
	PATH_BASED_SHAPES,
	NOTE,
	JUNCTION,
	AND_JUNCTION,
	OR_JUNCTION,
	ARCHIMATE_GROUP,
	STICKER,
	STICKER_DEFAULT_SIZE,
	buildX6RouterConfig,
} from './modelerX6/constants';
import {isCanvasOnlyNodeType, DEFAULT_STICKER_FILL_RGB} from './util/CanvasNodeTypes';
import * as viewHelpers from './modelerX6/viewHelpers';
import * as viewElements from './modelerX6/viewElements';
import * as nodeConfig from './modelerX6/nodeConfig';
import NodeFactory from './modelerX6/NodeFactory';
import EdgeFactory from './modelerX6/EdgeFactory';
import * as connectionModel from './modelerX6/ConnectionModel';
import * as importPrep from './modelerX6/importPrep';
import * as connectionLabelEditor from './modelerX6/connectionLabelEditor';
import * as renderView from './modelerX6/renderView';
import {PluginFactory, registerDefaultPlugins} from './plugins';
import {toRgbaString} from '@core_x6/lib/draw';
import {toRgba as hexToRgbaObj} from './util/ColorUtil';
import {installBoxSelectMode} from './boxSelectMode';
import {applyViewpointDimming} from './viewpoint/applyViewpointDimming';
import {captureBoardClipboard, pasteBoardClipboard} from './modelerX6/boardCopyPaste';
import {
	CONNECTION_DRAG_INVALID_FILTER_SELECTORS,
	ensureInvalidConnectionTargetFilter,
} from './modelerX6/invalidConnectionTargetFilter';

// Регистрация node tool «Связь» (реализация в tools/connectNodeTool.js)
nodeToolRegistry.register({connect: connectNodeTool}, true);

export default class ModelerX6 extends ViewerX6 {
	constructor(options = {}) {
		const opts = {
			...options,
			getValidateConnection:
				typeof options.getValidateConnection === 'function'
					? options.getValidateConnection
					: modeler => args => modeler._validateConnectionForGraph(args),
		};
		super(opts);
		this._options = options;
		this._moddle = new Moddle(options.moddleExtensions || {});
		this._moddle.ids = new Ids();
		this._pathMap = new PathMap();
		this._nodeFactory = new NodeFactory(this);
		this._edgeFactory = new EdgeFactory(this);
		this._pluginFactory = new PluginFactory(this);
		registerDefaultPlugins(this._pluginFactory);
		this._x6ModelingAdapter = {
			updateProperties: (element, payload) => this._updateConceptProperties(element, payload),
			updateViewElementAppearance: (viewElement, payload) =>
				this._updateViewElementAppearance(viewElement, payload),
			updateViewElementLabel: (element, text) => this._updateViewElementLabel(element, text),
		};
		this._stencilSelectedRelationshipType = null;
		this._boxSelectModeCleanup = null;
		/** Тип маршрутизации связей: X6 `normal` (произвольный) или `manhattan`. */
		this._connectionRouter = 'normal';
		/** @type {{ version: number, nodes: unknown[], edges: unknown[] } | null} */
		this._boardClipboard = null;
	}

	getConnectionRouter() {
		return this._connectionRouter === 'manhattan' ? 'manhattan' : 'normal';
	}

	/**
	 * Устанавливает тип маршрутизации связей для новых и существующих рёбер.
	 * @param {'normal'|'orth'|'manhattan'} name — normal (произвольный, X6 router normal), orth — устаревший синоним normal, manhattan
	 */
	setConnectionRouter(name) {
		const next = name === 'manhattan' ? 'manhattan' : 'normal';
		if (this._connectionRouter === next) return;
		this._connectionRouter = next;
		const graph = this.getGraph();
		if (graph?.options?.connecting) {
			graph.options.connecting.router = buildX6RouterConfig(next);
		}
		// Снять tools с рёбер: при manhattan не показываем vertices/segments — старые ручки не должны остаться.
		graph?.getEdges?.()?.forEach(edge => {
			try {
				graph.findViewByCell?.(edge)?.removeTools?.();
			} catch {
				//
			}
		});
		const edges = graph?.getEdges?.() ?? [];
		const routerCfg = buildX6RouterConfig(next);
		const prevSuppress = this._suppressConnectionPatchSync;
		this._suppressConnectionPatchSync = true;
		try {
			edges.forEach(edge => {
				try {
					if (typeof edge.setRouter === 'function') edge.setRouter(routerCfg);
				} catch {
					//
				}
			});
		} finally {
			this._suppressConnectionPatchSync = prevSuppress;
		}
	}

	/**
	 * Рамочное выделение с палитры: отключает panning и подключает свою рамку (rubberband плагина X6 с panning даёт пустое выделение).
	 */
	enablePaletteBoxSelectMode() {
		this.disablePaletteBoxSelectMode();
		const graph = this.getGraph();
		const sel = this.getSelection();
		if (!graph || !sel) return;
		sel.disableRubberband();
		graph.panning.disablePanning();
		this._boxSelectModeCleanup = installBoxSelectMode(graph, sel);
	}

	/** Выйти из режима рамки, снова включить панорамирование. */
	disablePaletteBoxSelectMode() {
		if (typeof this._boxSelectModeCleanup === 'function') {
			this._boxSelectModeCleanup();
			this._boxSelectModeCleanup = null;
		}
		const graph = this.getGraph();
		if (graph?.panning) {
			graph.panning.enablePanning();
		}
	}

	/** Очистить стек undo/redo (после смены вида уже вызывается из renderView). */
	cleanGraphHistory() {
		this.getGraph()?.cleanHistory?.();
	}

	/** Плагин History: `canUndo`/`canRedo`, `disable()`/`enable()`, `clean()`. */
	getHistoryPlugin() {
		return this.getGraph()?.getPlugin?.('history') ?? null;
	}

	/**
	 * Снять selection и виджет ресайза до removeCell, иначе ручка остаётся на холсте.
	 * @param {import('@antv/x6').Cell | null | undefined} cell
	 */
	_releaseCellChrome(cell) {
		const graph = this.getGraph();
		if (!graph || !cell) return;
		try {
			graph.unselect?.(cell);
			cell.removeTools?.();
			graph.clearTransformWidgets?.();
		} catch (_) {
			// виджет уже снят
		}
	}

	/**
	 * Живой список moddle. Одиночный объект оборачивается и записывается обратно, чтобы splice менял модель.
	 * @param {object | null | undefined} holder
	 * @param {string} key
	 * @param {unknown} raw
	 * @returns {Array}
	 */
	_liveList(holder, key, raw) {
		const current = raw !== undefined ? raw : holder?.[key];
		if (Array.isArray(current)) {
			if (holder && holder[key] !== current) {
				if (holder.set) holder.set(key, current);
				else holder[key] = current;
			}
			return current;
		}
		const list = current ? [current] : [];
		if (holder) {
			if (holder.set) holder.set(key, list);
			else holder[key] = list;
		}
		return list;
	}

	/**
	 * Преобразует цвет (hex, rgba-строка или {r,g,b,a}) в объект для moddle {r,g,b,a}, a 0–100.
	 * @param {string|{r: number, g: number, b: number, a?: number}} value
	 * @returns {{r: number, g: number, b: number, a: number} | null}
	 */
	_parseColorToModdle(value) {
		if (value == null) return null;
		if (
			typeof value === 'object' &&
			typeof value.r === 'number' &&
			typeof value.g === 'number' &&
			typeof value.b === 'number'
		) {
			const a = value.a != null ? Math.min(100, Math.max(0, Number(value.a))) : 100;
			return {r: value.r, g: value.g, b: value.b, a: Math.round(a)};
		}
		if (typeof value !== 'string') return null;
		const s = value.trim();
		if (s.startsWith('#')) {
			const obj = hexToRgbaObj(s);
			return obj
				? {
						r: Number(obj.r),
						g: Number(obj.g),
						b: Number(obj.b),
						a: obj.a != null ? Math.round(Number(obj.a)) : 100,
					}
				: null;
		}
		const rgbaMatch =
			/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)/.exec(s);
		if (rgbaMatch) {
			const a = rgbaMatch[4] != null ? Math.round(parseFloat(rgbaMatch[4]) * 100) : 100;
			return {
				r: Math.min(255, Math.max(0, parseInt(rgbaMatch[1], 10))),
				g: Math.min(255, Math.max(0, parseInt(rgbaMatch[2], 10))),
				b: Math.min(255, Math.max(0, parseInt(rgbaMatch[3], 10))),
				a: Math.min(100, Math.max(0, a)),
			};
		}
		return null;
	}

	/**
	 * Обновляет внешний вид элемента (стили viewElement) и перерисовывает ячейку на графе.
	 * @param {object} viewElement — view-элемент (Node или Connection)
	 * @param {{ fillColor?: string|object, lineColor?: string|object, textAlign?: string, lineWidth?: number }} payload
	 */
	_updateViewElementAppearance(viewElement, payload) {
		if (!viewElement || !payload || typeof payload !== 'object') return;
		const moddle = this._moddle;
		const graph = this.getGraph();
		if (!moddle || !graph) return;

		const viewId = viewElement?.id ?? viewElement?.$attrs?.id;
		if (!viewId) return;

		const isConnection =
			viewElement?.$type === 'archimate:Connection' ||
			(viewElement?.relationshipRef != null && viewElement?.elementRef == null);

		if (!viewElement.style) {
			viewElement.style = moddle.create('archimate:Style', {});
		}
		const style = viewElement.style;
		const setStyle = (key, val) => {
			if (style?.set) style.set(key, val);
			else if (style) style[key] = val;
		};

		if (payload.fillColor !== undefined && !isConnection) {
			const parsed = this._parseColorToModdle(payload.fillColor);
			if (parsed) {
				style.fillColor = moddle.create('archimate:FillColor', parsed);
			}
		}
		if (payload.lineColor !== undefined) {
			const parsed = this._parseColorToModdle(payload.lineColor);
			if (parsed) {
				style.lineColor = moddle.create('archimate:LineColor', parsed);
			}
		}
		if (payload.textAlign !== undefined && !isConnection) {
			setStyle('textAlign', payload.textAlign === '' ? undefined : payload.textAlign);
		}
		if (payload.lineWidth !== undefined && payload.lineWidth !== null) {
			const w = Number(payload.lineWidth);
			setStyle('lineWidth', Number.isFinite(w) && w >= 0 ? w : undefined);
		}

		if (isConnection) {
			const edge = graph.getCellById?.(viewId) ?? graph.model?.getCellById?.(viewId);
			if (edge && typeof edge.isEdge === 'function' && edge.isEdge()) {
				const relationshipType =
					renderView.getRelationshipType?.(this, viewElement) ?? 'Association';
				const accessType = renderView.getRelationshipAccessType?.(this, viewElement);
				const lineColorRgba = style.lineColor ? toRgbaString(style.lineColor) : undefined;
				const lineWidth = style.lineWidth != null ? style.lineWidth : undefined;
				this._edgeFactory.update(edge, {
					lineColor: lineColorRgba,
					lineWidth,
					relationshipType,
					accessType,
				});
			}
		} else {
			const cell = graph.getCellById?.(viewId) ?? graph.model?.getCellById?.(viewId);
			if (cell && typeof cell.isNode === 'function' && cell.isNode()) {
				this._nodeFactory.update(cell);
			}
		}

		// Отправка патча в сокет для синхронизации внешнего вида
		if (typeof this._onPatchCallback !== 'function') return;
		const viewIndex = this._currentViewIndex ?? 0;
		const toColorDto = c => {
			if (!c) return undefined;
			const r = c.r != null ? Number(c.r) : undefined;
			const g = c.g != null ? Number(c.g) : undefined;
			const b = c.b != null ? Number(c.b) : undefined;
			const a = c.a != null ? Number(c.a) : 100;
			if (r === undefined || g === undefined || b === undefined) return undefined;
			return {r, g, b, a};
		};
		const patches = [];
		const hasColor = payload.fillColor !== undefined || payload.lineColor !== undefined;
		if (hasColor && style) {
			const fillDto =
				!isConnection && style.fillColor ? toColorDto(style.fillColor) : undefined;
			const lineDto = style.lineColor ? toColorDto(style.lineColor) : undefined;
			if (fillDto || lineDto) {
				patches.push({
					op: 'updateNode',
					viewIndex,
					identifier: viewId,
					...(fillDto && {fillColor: fillDto}),
					...(lineDto && {lineColor: lineDto}),
				});
			}
		}
		if (!isConnection && payload.textAlign !== undefined) {
			patches.push({
				op: 'updateNodeStyles',
				viewIndex,
				identifier: viewId,
				styles: {textAlign: style?.textAlign ?? ''},
			});
		}
		if (patches.length > 0) {
			this._onPatchCallback({patches});
		}
	}

	/**
	 * Обновляет подпись view-элемента без концепта (стикер и т.п.) и отправляет updateElementLabel.
	 * @param {{ businessObject: object }} element
	 * @param {string} text
	 */
	_updateViewElementLabel(element, text) {
		const viewElement = element?.businessObject;
		if (!viewElement) return;
		const newText = text ?? '';
		viewElement.label = newText;
		const viewElementId = viewElement?.id ?? viewElement?.$attrs?.id;
		if (!viewElementId) return;
		const graph = this.getGraph();
		const cell = graph?.getCellById?.(viewElementId);
		if (cell && typeof cell.isNode === 'function' && cell.isNode()) {
			if (typeof this._nodeFactory?.update === 'function') {
				this._nodeFactory.update(cell, {label: newText});
			} else {
				cell.setAttrByPath?.('label/text', newText);
			}
		}
		if (typeof this._onPatchCallback !== 'function') return;
		this._onPatchCallback({
			patches: [
				{
					op: 'updateElementLabel',
					viewIndex: this._currentViewIndex ?? 0,
					identifier: viewElementId,
					elementName: newText,
				},
			],
		});
	}

	/**
	 * Совместимость с API core (PropertiesModal): get('moddle'), get('modeling').
	 * @param {string} key
	 */
	get(key) {
		if (key === 'moddle') return this._moddle;
		if (key === 'modeling') return this._x6ModelingAdapter;
		return undefined;
	}

	/**
	 * Обновление свойств концепта (elementRef/relationshipRef) из payload и отображения на графе.
	 * Вызывается из PropertiesModal через modeling.updateProperties(element, payload).
	 * @param {{ businessObject: object }} element — элемент с businessObject = viewElement
	 * @param {{ name?: string, documentation?: string, properties?: object }} payload
	 */
	_updateConceptProperties(element, payload) {
		const viewElement = element?.businessObject;
		if (!viewElement || !payload || typeof payload !== 'object') return;
		const concept =
			viewHelpers.getElementRef(this, viewElement) ?? this._getRelationshipRef(viewElement);
		if (!concept) return;
		const setVal = (obj, key, val) => {
			if (obj?.set) obj.set(key, val);
			else if (obj) obj[key] = val;
		};
		if (payload.name !== undefined) {
			setVal(concept, 'name', payload.name);
			viewElement.label = payload.name;
		}
		if (payload.documentation !== undefined)
			setVal(concept, 'documentation', payload.documentation);
		if (payload.properties !== undefined) setVal(concept, 'properties', payload.properties);
		// Смена типа Junction (And ↔ Or): обновляем концепт и отрисовку узла
		const junctionTypes = [AND_JUNCTION, OR_JUNCTION];
		const newJunctionType =
			payload.type === AND_JUNCTION || payload.type === OR_JUNCTION ? payload.type : null;
		const currentType = concept?.type ?? concept?.$type ?? '';
		if (newJunctionType && junctionTypes.includes(currentType)) {
			setVal(concept, 'type', newJunctionType);
			const viewElementId = viewElement?.id ?? viewElement?.$attrs?.id;
			const graph = this.getGraph();
			const cell = viewElementId && graph?.getCellById?.(viewElementId);
			if (cell && typeof this._nodeFactory?.update === 'function') {
				const fillColor = newJunctionType === OR_JUNCTION ? 'none' : '#000000';
				this._nodeFactory.update(cell, {fillColor});
			}
		}
		const viewElementId = viewElement?.id ?? viewElement?.$attrs?.id;
		if (!viewElementId) return;
		const graph = this.getGraph();
		const cell = graph?.getCellById?.(viewElementId);
		if (cell && payload.name !== undefined) {
			if (typeof cell.isNode === 'function' && cell.isNode()) {
				this._nodeFactory?.update?.(cell, {label: payload.name});
			} else if (typeof cell.isEdge === 'function' && cell.isEdge()) {
				const labels = cell.getLabels?.() ?? [];
				if (labels.length > 0) cell.removeLabelAt?.(0);
				cell.appendLabel?.({attrs: {text: {text: payload.name}}});
			}
		}
		// Отправка патча в сокет (как в core при element.updateProperties)
		if (typeof this._onPatchCallback === 'function') {
			const elementProperties = {};
			if (payload.name != null) elementProperties.name = payload.name;
			if (payload.documentation != null)
				elementProperties.documentation = payload.documentation;
			if (payload.type != null) elementProperties.type = payload.type;
			const serializedProps = this._serializePropertiesForPatch(payload.properties);
			if (serializedProps?.property?.length)
				elementProperties.property = serializedProps.property;
			if (Object.keys(elementProperties).length > 0) {
				const viewIndex = this._currentViewIndex ?? 0;
				this._onPatchCallback({
					patches: [
						{
							op: 'updateElementProperties',
							viewIndex,
							identifier: viewElementId,
							elementProperties,
						},
					],
				});
			}
		}
	}

	/**
	 * Сериализация moddle Properties в формат для патча (как DiagramSync.serializePropertiesForPatch).
	 * @param {object} [properties] — moddle Properties с property: Array
	 * @returns {{ property: Array } | null}
	 */
	_serializePropertiesForPatch(properties) {
		if (!properties) return null;
		const propList = properties.property;
		if (!Array.isArray(propList)) return null;
		const arr = propList
			.map(pr => {
				const ref = pr?.propertyDefinitionRef;
				const defId = (ref && (typeof ref === 'object' ? ref.id : ref)) || null;
				if (!defId) return null;
				const defName =
					ref && typeof ref === 'object' ? (ref.name?.value ?? ref.name) : defId;
				const defType = ref && typeof ref === 'object' ? (ref.type ?? 'string') : 'string';
				const val = Array.isArray(pr.value) ? pr.value[0] : pr.value;
				return {
					propertyDefinitionRef: {id: defId, name: defName || defId, type: defType},
					value: val != null ? String(val) : null,
				};
			})
			.filter(Boolean);
		return arr.length ? {property: arr} : null;
	}

	_getRelationshipRef(viewElement) {
		const ref = viewElement?.relationshipRef ?? viewElement?.$attrs?.relationshipRef;
		if (!ref) return null;
		if (typeof ref === 'object') return ref;
		const rels =
			this._model?.relationshipsNode?.relationship ??
			this._model?.relationships?.relationship ??
			[];
		const list = Array.isArray(rels) ? rels : [rels].filter(Boolean);
		return list.find(r => (r?.id ?? r?.$attrs?.id) === ref) ?? null;
	}

	/**
	 * Текст для tooltip при наведении на узел или связь: первая строка — название, вторая — «Тип: {type}».
	 * @param {import('@antv/x6').Node|import('@antv/x6').Edge} cell
	 * @returns {string}
	 */
	_getTooltipTextForCell(cell) {
		if (!cell?.id) return '';
		const viewElement = this._findViewElementById(cell.id);
		if (!viewElement) return '';
		const concept =
			viewHelpers.getElementRef(this, viewElement) ?? this._getRelationshipRef(viewElement);
		if (!concept) return viewHelpers.getLabel(this, viewElement) || cell.id;
		const typeLabel = this._formatTypeLabel(concept.type ?? concept?.$type ?? '') || '—';
		const name =
			viewElement?.label ??
			concept?.name ??
			(typeof concept.name === 'object' ? concept.name?.value : null) ??
			typeLabel ??
			'—';
		const nameStr = typeof name === 'string' ? name : (name?.value ?? '—');
		const firstLine = nameStr?.trim() || typeLabel || '—';
		return `${firstLine}\nТип: ${typeLabel}`;
	}

	_formatTypeLabel(type) {
		if (type == null || type === '') return '—';
		const raw = typeof type === 'string' ? type : String(type ?? '');
		const withoutNs = raw.replace(/^archimate:/i, '').trim();
		return withoutNs ? withoutNs.replace(/([A-Z])/g, ' $1').trim() : '—';
	}

	_getConceptDisplayName(concept) {
		if (!concept) return '—';
		let obj = concept;
		if (typeof concept === 'string' && this._model) {
			const elements =
				this._model.elementsNode?.element ?? this._model.elementsNode?.elements ?? [];
			const list = Array.isArray(elements) ? elements : [elements].filter(Boolean);
			obj = list.find(e => (e?.id ?? e?.$attrs?.id) === concept) ?? concept;
		}
		const n = obj?.name;
		const name = n != null ? (typeof n === 'string' ? n : n?.value) : null;
		return (
			name ||
			this._formatTypeLabel(obj?.type) ||
			(typeof concept === 'string' ? concept : '—')
		);
	}

	attachTo(container) {
		super.attachTo(container);
		const graph = this.getGraph();
		// Валидатор X6 connecting.validateConnection не вызывается при нашем сценарии: связь создаётся
		// через startConnectionDrag (инструмент «Связь» + перетаскивание), а не перетаскиванием с магнита узла.
		// Проверка правил ArchiMate выполняется в finishUp и в edgeConnectedPlugin через _canConnectViewNodes.
		if (graph?.options?.connecting) {
			graph.options.connecting.validateConnection = args =>
				this._validateConnectionForGraph(args);
			graph.options.connecting.router = buildX6RouterConfig(this.getConnectionRouter());
		}
		if (graph) {
			this._nodeAddedFromStencilHandler = args => this._handleNodeAddedFromStencil(args);
			graph.on('node:added', this._nodeAddedFromStencilHandler);
			this._installCellTooltip(graph);
		}
		// Палитра рендерится отдельным React-компонентом (ArchiX6Palette), Stencil не используется
	}

	/**
	 * Показ tooltip при наведении на узел или связь.
	 * Только положение курсора: X6 mouseenter/mouseleave после выделения нестабильны,
	 * поэтому ячейка под курсором определяется через elementsFromPoint + graph.findView.
	 * @param {import('@antv/x6').Graph} graph
	 */
	_installCellTooltip(graph) {
		const TOOLTIP_OFFSET = 12;
		const TOOLTIP_DELAY_MS = 400;
		let showTimer = null;
		/** @type {string|null} id ячейки под курсором (последние координаты) */
		let hoverCellId = null;
		let moveRaf = null;
		let pendingX = 0;
		let pendingY = 0;
		const el = document.createElement('div');
		el.className = 'x6-cell-tooltip';
		el.setAttribute('role', 'tooltip');
		Object.assign(el.style, {
			position: 'fixed',
			zIndex: 10000,
			maxWidth: '320px',
			padding: '6px 10px',
			fontSize: '12px',
			lineHeight: 1.4,
			whiteSpace: 'pre-line',
			color: '#fff',
			backgroundColor: 'rgba(0,0,0,0.75)',
			borderRadius: '4px',
			pointerEvents: 'none',
			visibility: 'hidden',
			boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
		});
		document.body.appendChild(el);
		this._tooltipEl = el;

		const positionTooltipAtClient = (clientX, clientY) => {
			el.style.left = `${clientX + TOOLTIP_OFFSET}px`;
			el.style.top = `${clientY + TOOLTIP_OFFSET}px`;
			const rect = el.getBoundingClientRect();
			if (rect.right > window.innerWidth)
				el.style.left = `${Math.max(8, window.innerWidth - rect.width - 8)}px`;
			if (rect.bottom > window.innerHeight)
				el.style.top = `${Math.max(8, window.innerHeight - rect.height - 8)}px`;
		};

		const clearShowTimer = () => {
			if (showTimer) {
				clearTimeout(showTimer);
				showTimer = null;
			}
		};

		const hide = () => {
			clearShowTimer();
			el.style.visibility = 'hidden';
		};

		const getCellIdUnderCursor = (clientX, clientY) => {
			const container = graph.container;
			if (!container) return null;
			const stack = document.elementsFromPoint(clientX, clientY);
			for (const elem of stack) {
				if (!container.contains(elem)) continue;
				const view = graph.findView(elem);
				if (view?.cell?.id) return view.cell.id;
			}
			return null;
		};

		const resolveHover = (clientX, clientY) => {
			const hitCellId = getCellIdUnderCursor(clientX, clientY);

			if (hitCellId === hoverCellId) {
				if (el.style.visibility === 'visible' && hitCellId) {
					positionTooltipAtClient(clientX, clientY);
				}
				return;
			}

			hoverCellId = hitCellId;
			hide();

			if (!hitCellId) return;

			const cell = graph.getCellById(hitCellId);
			if (!cell) return;

			const scheduledForId = hitCellId;
			showTimer = setTimeout(() => {
				showTimer = null;
				if (hoverCellId !== scheduledForId) return;
				const text = this._getTooltipTextForCell(cell);
				if (!text) return;
				el.textContent = text;
				el.style.visibility = 'visible';
				positionTooltipAtClient(pendingX, pendingY);
			}, TOOLTIP_DELAY_MS);
		};

		const onContainerMouseMove = e => {
			pendingX = e.clientX;
			pendingY = e.clientY;
			if (moveRaf != null) return;
			moveRaf = requestAnimationFrame(() => {
				moveRaf = null;
				resolveHover(pendingX, pendingY);
			});
		};

		const onContainerMouseLeave = () => {
			hoverCellId = null;
			if (moveRaf != null) {
				cancelAnimationFrame(moveRaf);
				moveRaf = null;
			}
			hide();
		};

		const dispose = () => {
			clearShowTimer();
			if (moveRaf != null) {
				cancelAnimationFrame(moveRaf);
				moveRaf = null;
			}
			hoverCellId = null;
			el.style.visibility = 'hidden';
		};

		const container = graph.container;
		if (container) {
			container.addEventListener('mousemove', onContainerMouseMove);
			container.addEventListener('mouseleave', onContainerMouseLeave);
		}

		this._tooltipHandlers = {
			container,
			onContainerMouseMove,
			onContainerMouseLeave,
			dispose,
		};
	}

	_uninstallCellTooltip() {
		if (this._tooltipHandlers) {
			const h = this._tooltipHandlers;
			if (h.container) {
				h.container.removeEventListener('mousemove', h.onContainerMouseMove);
				h.container.removeEventListener('mouseleave', h.onContainerMouseLeave);
			}
			if (typeof h.dispose === 'function') h.dispose();
			this._tooltipHandlers = null;
		}
		if (this._tooltipEl?.parentNode) {
			this._tooltipEl.parentNode.removeChild(this._tooltipEl);
			this._tooltipEl = null;
		}
	}

	detach() {
		const graph = this.getGraph();
		if (graph && this._nodeAddedFromStencilHandler) {
			graph.off('node:added', this._nodeAddedFromStencilHandler);
			this._nodeAddedFromStencilHandler = null;
		}
		this._uninstallCellTooltip();
		this._paletteContainer = null;
		super.detach();
	}

	/**
	 * При добавлении узла из палитры (_fromStencil): выставляем минимальный размер,
	 * добавляем узел в модель (element + viewElement), чтобы от него можно было тянуть связь.
	 */
	_handleNodeAddedFromStencil(args) {
		const node = args?.node ?? args?.cell ?? args;
		if (!node || typeof node.isNode !== 'function' || !node.isNode()) return;
		const data = node.getData?.();
		if (!data?._fromStencil) return;
		const shape = typeof node.getShape === 'function' ? node.getShape() : node.shape;
		const isJunctionShape = ['archimate:and-junction', 'archimate:or-junction'].includes(shape);

		const size = node.getSize?.() ?? {};
		const w = size.width ?? 60;
		const h = size.height ?? 32;
		if (!isJunctionShape) {
			const newW = Math.max(w, MIN_ELEMENT_WIDTH);
			const newH = Math.max(h, getMinElementHeightForShape(shape));
			if (newW > w || newH > h) {
				node.resize?.(newW, newH);
			}
		}

		// Path-based формы (motivation hexagon, note): после ресайза путь пересчитывается в nodeResizePlugin,
		// но при добавлении с палитры событие change:size может не обновить path — принудительно обновляем.
		if (PATH_BASED_SHAPES.includes(shape) && typeof this._nodeFactory?.update === 'function') {
			this._nodeFactory.update(node);
		}

		let elementType = data.elementType;
		if (!elementType && shape) {
			const kebab = String(shape).replace(/^archimate:/, '');
			elementType = kebab
				.split('-')
				.map(s => s.charAt(0).toUpperCase() + (s.slice(1) || '').toLowerCase())
				.join('');
		}
		elementType = elementType ? viewHelpers.normalizeType(elementType) : '';
		if (elementType) {
			this._addStencilNodeToModel(node, elementType, data.typeName);
		}
		node.setData?.({...data, _fromStencil: undefined});
	}

	/**
	 * Добавляет узел, сброшенный с палитры, в модель (element + viewElement), чтобы _findViewElementById находил его и от него можно было тянуть связь.
	 */
	_addStencilNodeToModel(node, elementType, typeNameLabel) {
		const view = this._currentView;
		const model = this._model;
		const moddle = this._moddle;
		if (!view || !model || !moddle) return;

		const nodeId = node.id;
		if (!nodeId) return;
		// Уже в модели — не дублировать (node:added иногда может срабатывать повторно)
		if (this._findViewElementById(nodeId)) return;

		const pos = node.getPosition?.() ?? {x: 0, y: 0};
		const size = node.getSize?.() ?? {};
		const isJunction = [JUNCTION, AND_JUNCTION, OR_JUNCTION].includes(elementType);
		const isCanvasOnly = isCanvasOnlyNodeType(elementType);
		const w = isJunction
			? 20
			: isCanvasOnly
				? (size.width ?? STICKER_DEFAULT_SIZE.width)
				: (size.width ?? 120);
		const h = isJunction
			? 20
			: isCanvasOnly
				? (size.height ?? STICKER_DEFAULT_SIZE.height)
				: (size.height ?? 55);

		if (isCanvasOnly) {
			const viewNode = moddle.create('archimate:Node', {
				id: nodeId,
				type: elementType,
				x: Math.round(pos.x ?? 0),
				y: Math.round(pos.y ?? 0),
				w,
				h,
			});
			const fill = DEFAULT_STICKER_FILL_RGB;
			const style = moddle.create('archimate:Style', {});
			style.fillColor = moddle.create('archimate:FillColor', fill);
			viewNode.style = style;
			viewNode.label = '';
			if (moddle.ids?.claim) moddle.ids.claim(nodeId, viewNode);
			viewNode.$parent = view;
			if (!view.viewElements) view.viewElements = [];
			const getElId = o => o?.id ?? o?.$attrs?.id;
			const idx = view.viewElements.findIndex(el => getElId(el) === nodeId);
			if (idx >= 0) view.viewElements.splice(idx, 1);
			view.viewElements.push(viewNode);
			this.getGraph()?.emit?.('node:added-to-view', {
				node: {
					identifier: nodeId,
					x: Math.round(pos.x ?? 0),
					y: Math.round(pos.y ?? 0),
					w,
					h,
					type: elementType,
					label: '',
					fillColor: fill,
				},
			});
			return;
		}

		const elementsNode = model.elementsNode || model.elements;
		if (!elementsNode) return;
		const elementsArr = elementsNode.element || elementsNode.elements;
		const elements = Array.isArray(elementsArr)
			? elementsArr
			: elementsArr
				? [elementsArr]
				: [];
		const elemId = moddle.ids?.nextPrefixed?.('id-elem-', node) ?? `id-elem-${Date.now()}`;
		// Палитра раньше передавала typeName = archimate:Group — в name уходил тип, а на канве был «Group».
		const labelFromTypeName =
			typeNameLabel != null &&
			String(typeNameLabel).trim() !== '' &&
			typeNameLabel !== ARCHIMATE_GROUP
				? typeNameLabel
				: null;
		const defaultName =
			labelFromTypeName ?? this._getDefaultLabelForType?.(elementType) ?? elementType;
		const elem = moddle.create('archimate:BaseElement', {
			id: elemId,
			type: elementType,
			name: defaultName,
		});
		if (moddle.ids?.claim) moddle.ids.claim(elemId, elem);
		elem.$parent = elementsNode;
		const newElements = [...elements, elem];
		if (elementsNode.set) elementsNode.set('element', newElements);
		else elementsNode.element = newElements;

		if (this._elementsById) this._elementsById[elemId] = elem;

		const viewNode = moddle.create('archimate:Node', {
			id: nodeId,
			elementRef: elem,
			x: Math.round(pos.x ?? 0),
			y: Math.round(pos.y ?? 0),
			w: w,
			h: h,
		});
		if (moddle.ids?.claim) moddle.ids.claim(nodeId, viewNode);
		viewNode.$parent = view;
		if (!view.viewElements) view.viewElements = [];
		// Убедиться, что в view нет другого элемента с тем же id (иначе при отрисовке получится дубликат на канве)
		const getElId = o => o?.id ?? o?.$attrs?.id;
		const idx = view.viewElements.findIndex(el => getElId(el) === nodeId);
		if (idx >= 0) view.viewElements.splice(idx, 1);
		view.viewElements.push(viewNode);

		const graph = this.getGraph();
		graph?.emit?.('node:added-to-view', {
			node: {
				identifier: nodeId,
				elementRef: elemId,
				x: Math.round(pos.x ?? 0),
				y: Math.round(pos.y ?? 0),
				w,
				h,
				type: elementType,
				name: defaultName,
			},
		});
	}

	/**
	 * Список групп палитры с элементами для отображения в своей палитре (React и т.д.).
	 * @returns {Array<{ name: string, title: string, entries: Array<{ type: string, typeName: string, width: number, height: number, iconRef?: string | null }> }>}
	 */
	static getPaletteGroups() {
		const result = [
			{
				name: 'canvas',
				title: 'Доска',
				entries: [
					{
						type: STICKER,
						typeName: 'Стикер',
						width: STICKER_DEFAULT_SIZE.width,
						height: STICKER_DEFAULT_SIZE.height,
						iconRef: null,
						shape: 'sticker',
					},
				],
			},
		];
		for (const {name, title, map} of STENCIL_GROUPS) {
			const entries = [];
			if (map) {
				const addedKeys = new Set();
				for (const [type, entry] of map.entries()) {
					if (
						!entry?.typeName &&
						!entry?.pictoRef &&
						!entry?.pathMap &&
						!entry?.figureRef
					)
						continue;
					if (
						type === JUNCTION ||
						type === AND_JUNCTION ||
						type === OR_JUNCTION ||
						type === NOTE
					)
						continue;
					const key = `${entry.typeName ?? type}|${entry.pictoRef ?? ''}|${entry.pathMap ?? ''}|${entry.figureRef ?? ''}`;
					if (addedKeys.has(key)) continue;
					addedKeys.add(key);
					const iconRef = entry.pictoRef || entry.pathMap || entry.figureRef || null;
					entries.push({
						type,
						typeName: entry.typeName || type,
						width: 60,
						height: 32,
						iconRef,
						shape: 'path',
					});
				}
				if (name === 'other') {
					entries.push({
						type: NOTE,
						typeName: NOTE,
						width: 55,
						height: 35,
						iconRef: 'FIGURE_NOTE',
						shape: 'path',
					});
					entries.push({
						type: AND_JUNCTION,
						typeName: AND_JUNCTION,
						width: 14,
						height: 14,
						iconRef: null,
						shape: 'junction',
					});
					entries.push({
						type: ARCHIMATE_GROUP,
						// Человекочитаемое имя по умолчанию (как getDefaultLabelForType / подпись на канве), не тип archimate:Group.
						typeName: 'Group',
						width: 55,
						height: 35,
						iconRef: null,
						shape: 'group',
					});
				}
			}
			result.push({name, title, entries});
		}
		return result;
	}

	/**
	 * Path иконки для палитры (тот же picto/path, что рисуется в углу узла).
	 * @param {string} iconRef — ключ из PathMap (pictoRef/pathMap/figureRef)
	 * @param {number} [width]
	 * @param {number} [height]
	 * @returns {string | null} d для SVG path или null
	 */
	getIconSvgPath(iconRef, width = 24, height = 24) {
		if (!iconRef || !this._pathMap?.getScaledPath) return null;
		try {
			return this._pathMap.getScaledPath(iconRef, {
				xScaleFactor: 1,
				yScaleFactor: 1,
				containerWidth: width,
				containerHeight: height,
				position: {mx: 0, my: 0},
			});
		} catch {
			return null;
		}
	}

	/**
	 * Цвета иконки по слою элемента (для палитры).
	 * @param {string} elementType
	 * @returns {{ fillColor: string, lineColor: string }}
	 */
	getIconColorsForType(elementType) {
		const normalized = viewHelpers.normalizeType(elementType);
		if (normalized === AND_JUNCTION) return {fillColor: '#000000', lineColor: '#000000'};
		if (normalized === OR_JUNCTION) return {fillColor: 'none', lineColor: '#000000'};
		if (normalized === STICKER) return {fillColor: '#FFF9B1', lineColor: 'none'};
		if (normalized === viewHelpers.normalizeType(ARCHIMATE_GROUP))
			return {fillColor: 'lightgray', lineColor: '#585858'};
		const nodeType = nodeTypeRegistry.get(normalized);
		if (!nodeType) return {fillColor: '#ffffff', lineColor: '#000000'};
		const fill =
			typeof nodeType.getDefaultFillColor === 'function'
				? nodeType.getDefaultFillColor()
				: '#ffffff';
		const line =
			typeof nodeType.getDefaultLineColor === 'function'
				? nodeType.getDefaultLineColor()
				: '#000000';
		return {fillColor: fill ?? '#ffffff', lineColor: line ?? '#000000'};
	}

	/**
	 * Запуск перетаскивания элемента с палитры (для своей палитры без Stencil). Создаёт узел и передаёт в Dnd.
	 * @param {string} elementType
	 * @param {{ typeName?: string, width?: number, height?: number }} [options]
	 * @param {MouseEvent|PointerEvent} evt
	 */
	startDragFromPalette(elementType, options, evt) {
		const graph = this.getGraph();
		const dnd = this.getDnd?.();
		if (!graph || !dnd?.start) return;
		const opts = {
			width: 60,
			height: 32,
			label: null,
			typeName: options?.typeName ?? elementType,
			...options,
		};
		let node;
		try {
			node = this.createElementNodePreview(elementType, opts);
		} catch {
			return;
		}
		node.setData?.({...node.getData?.(), _fromStencil: true});
		const shape = typeof node.getShape === 'function' ? node.getShape() : node.shape;
		const isJunction = shape && /junction/i.test(String(shape));
		const w = isJunction ? 20 : MIN_ELEMENT_WIDTH;
		const h = isJunction ? 20 : getMinElementHeightForShape(shape);
		if (typeof node.resize === 'function') node.resize(w, h);
		if (
			shape &&
			PATH_BASED_SHAPES.includes(shape) &&
			typeof this._nodeFactory?.update === 'function'
		) {
			this._nodeFactory.update(node);
		}
		dnd.start(node, evt);
	}

	/** Выбранный тип связи в палитре (для будущего магнита). */
	setSelectedRelationshipType(type) {
		this._stencilSelectedRelationshipType = type ?? null;
	}

	getSelectedRelationshipType() {
		return this._stencilSelectedRelationshipType ?? null;
	}

	createNewModel(diagramName) {
		const name =
			diagramName && String(diagramName).trim() ? diagramName.trim() : 'Новая диаграмма';
		return this.importXML(TEMPLATE_MODEL).then(result => {
			const parseWarnings = result.warnings || [];
			const newModel = this._model;
			const defaultView = newModel?.views?.diagrams?.viewsList?.[0];
			if (!defaultView) {
				return Promise.reject(new Error('Template model has no default view'));
			}
			const prefix = 'id-';
			if (this._moddle.ids?.nextPrefixed) {
				newModel.id = this._moddle.ids.nextPrefixed(prefix, newModel);
				defaultView.id = this._moddle.ids.nextPrefixed(prefix, defaultView);
			} else {
				newModel.id = `id-model-${Date.now()}`;
				defaultView.id = `id-view-${Date.now()}`;
			}
			if (newModel.set) newModel.set('name', name);
			else newModel.name = name;
			if (defaultView.set) defaultView.set('name', name);
			else defaultView.name = name;
			return this.openView(defaultView.id ?? defaultView).then(openResult => ({
				warnings: [].concat(parseWarnings, openResult?.warnings || []),
			}));
		});
	}

	createApplicationComponent({id, x, y, width, height, label} = {}) {
		return this.createApplicationNode('ApplicationComponent', {
			id,
			x,
			y,
			width,
			height,
			label,
		});
	}

	createApplicationNode(type, {id, x, y, width, height, label, parent, viewElement} = {}) {
		return this._nodeFactory.createAndAdd(type, {
			id,
			x,
			y,
			width,
			height,
			label,
			parent,
			viewElement,
		});
	}

	createApplicationNodePreview(type, {width, height, label, typeName} = {}) {
		return this._nodeFactory.createPreview(type, {width, height, label, typeName});
	}

	createElementNode(type, {id, x, y, width, height, label, parent, viewElement} = {}) {
		return this.createApplicationNode(type, {
			id,
			x,
			y,
			width,
			height,
			label,
			parent,
			viewElement,
		});
	}

	createElementNodePreview(type, {width, height, label, typeName} = {}) {
		return this.createApplicationNodePreview(type, {width, height, label, typeName});
	}

	importXML(xml) {
		const normalizedXml = this._prepareForImport(xml);
		return this._moddle.fromXML(normalizedXml, ARCHIMATE_MODEL).then(result => {
			this._model = result.rootElement;
			this._elementsById = result.elementsById;
			this._elementsByIdentifier = this._collectElementsByIdentifier(this._model);
			if (result.warnings?.length) {
				console.warn(result.warnings);
			}
			return {warnings: result.warnings || []};
		});
	}

	importJSON(json) {
		const {model, elementsById} = buildModelFromJson(this._moddle, json);
		this._model = model;
		this._elementsById = elementsById;
		this._elementsByIdentifier = this._collectElementsByIdentifier(model);
		return Promise.resolve({warnings: []});
	}

	openView(viewOrId) {
		if (!this._model) {
			return Promise.reject(new Error('no model imported'));
		}

		const view = this._resolveView(viewOrId);
		if (!view) {
			return Promise.reject(new Error('no view to display'));
		}

		this._renderView(view);
		// Подгоняем видимую область под все элементы после отрисовки (следующий кадр, когда граф уже отрендерен)
		requestAnimationFrame(() => {
			this.fitViewport();
		});
		return Promise.resolve({warnings: []});
	}

	/**
	 * Подгоняет видимую область так, чтобы все элементы были видны.
	 */
	fitViewport() {
		const graph = this.getGraph();
		if (!graph) return;
		try {
			if (typeof graph.zoomToFit === 'function') {
				graph.zoomToFit({padding: 20});
			} else if (graph.transform?.zoomToFit) {
				graph.transform.zoomToFit({padding: 20});
			}
		} catch {
			//
		}
	}

	/**
	 * Копировать выделенные узлы (или один узел по anchorCellId) во внутренний буфер моделлера.
	 * @param {{ anchorCellId?: string, cellId?: string }} [options]
	 * @returns {boolean}
	 */
	copyBoardSelection(options = {}) {
		const graph = this.getGraph();
		if (!graph) return false;
		let cells =
			typeof graph.getSelectedCells === 'function'
				? graph
						.getSelectedCells()
						.filter(c => c && typeof c.isNode === 'function' && c.isNode())
				: [];
		const anchor = options.anchorCellId ?? options.cellId;
		if (cells.length === 0 && anchor) {
			const c = graph.getCellById?.(anchor);
			if (c && typeof c.isNode === 'function' && c.isNode()) cells = [c];
		}
		return captureBoardClipboard(this, cells);
	}

	/**
	 * Вставить ранее скопированные узлы (и связи между ними) со сдвигом.
	 * @returns {{ ok: boolean, nodeCount?: number, edgeCount?: number, reason?: string }}
	 */
	pasteBoardSelection(options = {}) {
		return pasteBoardClipboard(this, options);
	}

	/** @returns {boolean} */
	hasBoardClipboard() {
		return Boolean(this._boardClipboard?.nodes?.length);
	}

	/**
	 * Подписка на события X6 для отправки макро-патчей при изменении нод.
	 * @param {function({patches: Array}): void} onPatch
	 * @param {{onLive?: Function, onLock?: Function, clientSessionId?: string}|function} [collaboration]
	 * @returns {function(): void} отписка
	 */
	setupPatchSync(onPatch, collaboration) {
		if (typeof onPatch !== 'function') return () => {};
		const graph = this.getGraph();
		if (!graph) return () => {};

		const collab = collaboration && typeof collaboration === 'object' ? collaboration : {};
		this._onPatchCallback = onPatch;
		this._onLiveCallback = typeof collab.onLive === 'function' ? collab.onLive : null;
		this._onLockCallback = typeof collab.onLock === 'function' ? collab.onLock : null;
		this._clientSessionId = collab.clientSessionId ?? this._clientSessionId ?? null;
		if (!this._objectLocks) this._objectLocks = new Map();
		if (!this._committedGeometry) this._committedGeometry = new Map();
		if (!this._geometryGestureIds) this._geometryGestureIds = new Set();
		const context = {
			graph,
			onPatch,
			onLive: this._onLiveCallback,
			onLock: this._onLockCallback,
			viewIndex: this._currentViewIndex ?? 0,
		};
		const pluginCleanup = this._pluginFactory.setup(context);
		return () => {
			this._onPatchCallback = null;
			this._onLiveCallback = null;
			this._onLockCallback = null;
			this._closeLabelEditor();
			pluginCleanup();
		};
	}

	isLockedByOther(objectId) {
		if (!objectId || !this._objectLocks || !this._clientSessionId) return false;
		const lock = this._objectLocks.get(objectId);
		return Boolean(lock?.clientSessionId && lock.clientSessionId !== this._clientSessionId);
	}

	captureCommittedGeometry(objectId, geometry) {
		if (!objectId || !geometry) return;
		if (!this._committedGeometry) this._committedGeometry = new Map();
		this._committedGeometry.set(objectId, geometry);
	}

	applyLiveGeometry(payload) {
		if (!payload?.objectId) return;
		if (payload.clientSessionId && payload.clientSessionId === this._clientSessionId) return;
		if (payload.clientSessionId && this._draggingNodeId === payload.objectId) return;
		const graph = this.getGraph();
		const cell = graph?.getCellById?.(payload.objectId);
		const viewElement = this._findViewElementById?.(payload.objectId);
		this._applyingPatch = true;
		try {
			if (payload.kind === 'connection' || (payload.bendpoints && !payload.x && !payload.y)) {
				const bendpoints = Array.isArray(payload.bendpoints) ? payload.bendpoints : [];
				if (viewElement) this._updateConnectionWaypointsInModel(payload.objectId, bendpoints);
				if (cell?.setVertices) {
					this._updatingConnectionVerticesFromModel = payload.objectId;
					cell.setVertices(bendpoints.map(p => ({x: p.x ?? 0, y: p.y ?? 0})));
					this._updatingConnectionVerticesFromModel = null;
				}
				return;
			}
			if (viewElement) {
				const x = payload.x ?? viewElement.x ?? 0;
				const y = payload.y ?? viewElement.y ?? 0;
				const w = payload.w ?? viewElement.w ?? 120;
				const h = payload.h ?? viewElement.h ?? 60;
				viewElement.x = x;
				viewElement.y = y;
				viewElement.w = w;
				viewElement.h = h;
				if (viewElement.bounds) {
					viewElement.bounds.x = x;
					viewElement.bounds.y = y;
					viewElement.bounds.width = w;
					viewElement.bounds.height = h;
				} else {
					viewElement.bounds = {x, y, width: w, height: h};
				}
			}
			if (cell?.setPosition && (payload.x != null || payload.y != null)) {
				// deep: вложенные узлы едут вместе с родителем, как при локальном drag.
				// Их отдельные live не шлём — сервер отдаёт live только владельцу лока.
				cell.setPosition(payload.x ?? cell.getPosition?.().x ?? 0, payload.y ?? cell.getPosition?.().y ?? 0, {
					deep: true,
				});
				this._syncDescendantViewGeometry?.(cell);
			}
			if (cell?.setSize && (payload.w != null || payload.h != null)) {
				cell.setSize(payload.w ?? cell.getSize?.().width ?? 120, payload.h ?? cell.getSize?.().height ?? 60);
			}
		} finally {
			this._applyingPatch = false;
		}
	}

	_syncDescendantViewGeometry(cell) {
		const descendants =
			typeof cell?.getDescendants === 'function' ? cell.getDescendants({deep: true}) : [];
		descendants.forEach(child => {
			if (!child || typeof child.isNode !== 'function' || !child.isNode()) return;
			const pos = child.getPosition?.();
			if (!pos) return;
			const viewElement = this._findViewElementById?.(child.id);
			if (!viewElement) return;
			const size = child.getSize?.();
			const x = Math.round(pos.x);
			const y = Math.round(pos.y);
			const w = size ? Math.round(size.width) : (viewElement.w ?? viewElement.bounds?.width ?? 120);
			const h = size ? Math.round(size.height) : (viewElement.h ?? viewElement.bounds?.height ?? 60);
			viewElement.x = x;
			viewElement.y = y;
			viewElement.w = w;
			viewElement.h = h;
			if (viewElement.bounds) {
				viewElement.bounds.x = x;
				viewElement.bounds.y = y;
				viewElement.bounds.width = w;
				viewElement.bounds.height = h;
			} else {
				viewElement.bounds = {x, y, width: w, height: h};
			}
		});
	}

	applyLockEvent(lock) {
		if (!lock) return;
		if (!this._objectLocks) this._objectLocks = new Map();
		if (lock.action === 'snapshot') {
			this._objectLocks = new Map();
			(lock.locks ?? []).forEach(item => {
				if (item?.objectId) this._objectLocks.set(item.objectId, item);
			});
			return;
		}
		if (lock.action === 'release' && lock.objectId) {
			this._objectLocks.delete(lock.objectId);
			this._geometryGestureIds?.delete(lock.objectId);
			return;
		}
		if (lock.action === 'deny' && lock.objectId && lock.clientSessionId === this._clientSessionId) {
			this.revertCommittedGeometry(lock.objectId);
			this._geometryGestureIds?.delete(lock.objectId);
			if (this._draggingNodeId === lock.objectId) this._draggingNodeId = null;
			return;
		}
		if (lock.action === 'acquire' && lock.objectId) {
			this._objectLocks.set(lock.objectId, lock);
		}
	}

	revertCommittedGeometry(objectId) {
		const committed = this._committedGeometry?.get(objectId);
		if (!committed) return;
		this.applyLiveGeometry({
			objectId,
			kind: committed.kind,
			x: committed.x,
			y: committed.y,
			w: committed.w,
			h: committed.h,
			bendpoints: committed.bendpoints,
		});
	}

	/**
	 * Открывает редактор подписи связи (первый label). Вызывается при двойном клике по метке или из контекстного меню.
	 * @param {Object} cell — ячейка Edge
	 */
	_openConnectionLabelEditor(cell) {
		connectionLabelEditor.openConnectionLabelEditorForCell(this, cell);
	}

	openConnectionLabelEditor(connectionViewId) {
		connectionLabelEditor.openConnectionLabelEditor(this, connectionViewId);
	}

	_openLabelEditor(opts) {
		connectionLabelEditor.openLabelEditor(this, opts);
	}

	_closeLabelEditor() {
		connectionLabelEditor.closeLabelEditor(this);
	}

	_updateConnectionWaypointsInModel(connectionViewId, bendpoints) {
		connectionLabelEditor.updateConnectionWaypointsInModel(this, connectionViewId, bendpoints);
	}

	_getViewNodeMap() {
		return viewElements.getViewNodeMap(this);
	}

	/**
	 * Обновляет источник и/или цель связи. Обновляет модель (view connection + relationship) и edge на графе.
	 * @param {string} connectionViewId
	 * @param {{ sourceId?: string, targetId?: string }} endpoints — новые id view-узлов (только те, что меняем)
	 * @returns {boolean} успех
	 */
	updateConnectionEndpoints(connectionViewId, endpoints) {
		const graph = this.getGraph();
		const view = this._currentView;
		const viewElement = this._findViewElementById(connectionViewId);
		if (!graph || !view?.viewElements || !viewElement) return false;
		const getElementId = o => o?.id ?? o?.$attrs?.id;
		const nodeMap = this._getViewNodeMap();
		let sourceNode = viewElement.source
			? (nodeMap[getElementId(viewElement.source)] ?? viewElement.source)
			: null;
		let targetNode = viewElement.target
			? (nodeMap[getElementId(viewElement.target)] ?? viewElement.target)
			: null;
		if (endpoints.sourceId !== undefined) {
			sourceNode = nodeMap[endpoints.sourceId] ?? null;
			if (!sourceNode) return false;
		}
		if (endpoints.targetId !== undefined) {
			targetNode = nodeMap[endpoints.targetId] ?? null;
			if (!targetNode) return false;
		}
		if (!sourceNode || !targetNode) return false;
		if (!this._canConnectViewNodes(sourceNode, targetNode)) return false;

		const newSourceId = sourceNode.id ?? sourceNode.$attrs?.id ?? getElementId(sourceNode);
		const newTargetId = targetNode.id ?? targetNode.$attrs?.id ?? getElementId(targetNode);

		viewElement.source = sourceNode;
		viewElement.target = targetNode;
		const rel = viewElement.relationshipRef;
		if (rel) {
			rel.source = sourceNode?.elementRef ?? sourceNode?.$attrs?.elementRef ?? sourceNode;
			rel.target = targetNode?.elementRef ?? targetNode?.$attrs?.elementRef ?? targetNode;
			if (rel.set) {
				rel.set('source', rel.source);
				rel.set('target', rel.target);
			}
		}

		const edge = graph.getCellById?.(connectionViewId);
		if (edge && typeof edge.setSource === 'function' && typeof edge.setTarget === 'function') {
			this._updatingConnectionFromModel = connectionViewId;
			try {
				edge.setSource({cell: newSourceId, anchor: 'orth', connectionPoint: 'boundary'});
				edge.setTarget({cell: newTargetId, anchor: 'orth', connectionPoint: 'boundary'});
			} finally {
				this._updatingConnectionFromModel = null;
			}
		}
		// Патч updateConnection уходит из edgeTerminalChangePlugin по cell:change:source/target.
		return true;
	}

	/**
	 * Включает режим переподключения: следующий клик по узлу задаёт новый источник или цель связи.
	 * @param {string} connectionViewId
	 * @param {'source'|'target'} endpoint
	 * @param {function(): void} [onDone] — вызвается после успешного переподключения
	 * @returns {function(): void} отмена режима
	 */
	startReconnectEndpoint(connectionViewId, endpoint, onDone) {
		const graph = this.getGraph();
		if (!graph) return () => {};
		const viewElement = this._findViewElementById(connectionViewId);
		if (!viewElement) return () => {};
		const getElementId = o => o?.id ?? o?.$attrs?.id;
		const currentSourceId = viewElement.source ? getElementId(viewElement.source) : null;
		const currentTargetId = viewElement.target ? getElementId(viewElement.target) : null;

		const handler = args => {
			const cell = args?.cell ?? args?.view?.cell;
			if (!cell || typeof cell.isNode !== 'function' || !cell.isNode()) return;
			const nodeId = cell.id;
			if (nodeId === (endpoint === 'source' ? currentSourceId : currentTargetId)) return;
			const updates = endpoint === 'source' ? {sourceId: nodeId} : {targetId: nodeId};
			const ok = this.updateConnectionEndpoints(connectionViewId, updates);
			cleanup();
			if (ok && typeof onDone === 'function') onDone();
		};
		const cleanup = () => {
			graph.off('cell:click', handler);
			this._reconnectCleanup = null;
		};
		this._reconnectCleanup = cleanup;
		graph.on('cell:click', handler);
		return cleanup;
	}

	/**
	 * Режим создания связи из палитры: первый клик — источник, второй — цель.
	 * @param {function(string): void} [onStep] — onStep('source'|'target') при смене шага
	 * @returns {function(): void} отмена режима
	 */
	startConnectionMode(onStep) {
		const graph = this.getGraph();
		if (!graph) return () => {};
		this._connectionModeSource = null;
		const VIEW_INDEX = this._currentViewIndex ?? 0;

		const onCellClick = args => {
			const cell = args?.cell ?? args?.view?.cell;
			if (!cell || typeof cell.isNode !== 'function' || !cell.isNode()) return;
			const nodeId = cell.id;
			const viewNode = this._findViewElementById(nodeId);
			if (!viewNode) return;

			if (this._connectionModeSource == null) {
				this._connectionModeSource = nodeId;
				if (typeof onStep === 'function') onStep('target');
				return;
			}
			if (nodeId === this._connectionModeSource) return;
			this._createConnectionBetween(this._connectionModeSource, nodeId, VIEW_INDEX);
			cleanup();
		};
		const onBlankClick = () => cleanup();
		const cleanup = () => {
			graph.off('cell:click', onCellClick);
			graph.off('blank:click', onBlankClick);
			this._connectionModeSource = null;
			this._connectionModeCleanup = null;
		};
		this._connectionModeCleanup = cleanup;
		graph.on('cell:click', onCellClick);
		graph.on('blank:click', onBlankClick);
		if (typeof onStep === 'function') onStep('source');
		return cleanup;
	}

	/**
	 * Создать связь от узла (кнопка у элемента): следующий клик по узлу — цель.
	 * @param {string} sourceNodeId
	 * @param {function(): void} [onDone]
	 * @returns {function(): void} отмена
	 */
	startConnectionFrom(sourceNodeId, onDone) {
		const graph = this.getGraph();
		if (!graph) return () => {};
		this._connectionModeSource = sourceNodeId;
		const VIEW_INDEX = this._currentViewIndex ?? 0;

		const onCellClick = args => {
			const cell = args?.cell ?? args?.view?.cell;
			if (!cell || typeof cell.isNode !== 'function' || !cell.isNode()) return;
			const nodeId = cell.id;
			if (nodeId === sourceNodeId) return;
			this._createConnectionBetween(sourceNodeId, nodeId, VIEW_INDEX);
			cleanup();
			if (typeof onDone === 'function') onDone();
		};
		const onBlankClick = () => cleanup();
		const cleanup = () => {
			graph.off('cell:click', onCellClick);
			graph.off('blank:click', onBlankClick);
			this._connectionModeSource = null;
			this._connectionModeCleanup = null;
		};
		this._connectionModeCleanup = cleanup;
		graph.on('cell:click', onCellClick);
		graph.on('blank:click', onBlankClick);
		return cleanup;
	}

	/**
	 * Создаёт связь между двумя view-узлами (модель + edge + патч).
	 * @param {string} sourceNodeId
	 * @param {string} targetNodeId
	 * @param {number} viewIndex
	 * @returns {boolean} успех
	 */
	_createConnectionBetween(sourceNodeId, targetNodeId, viewIndex) {
		const graph = this.getGraph();
		const srcNode = this._findViewElementById(sourceNodeId);
		const tgtNode = this._findViewElementById(targetNodeId);
		if (!graph || !srcNode || !tgtNode) return false;
		if (!this._canConnectViewNodes(srcNode, tgtNode)) return false;
		const result = connectionModel.createConnection(
			this,
			srcNode,
			tgtNode,
			RELATIONSHIP_ASSOCIATION
		);
		if (!result) return false;
		const {connectionId, relationshipRefId, bendpoints} = result;
		const edge = this.addConnectionEdge({
			id: connectionId,
			source: sourceNodeId,
			target: targetNodeId,
			vertices: [],
			relationshipType: RELATIONSHIP_ASSOCIATION,
			lineColor: '#000000',
		});
		if (edge) edge.setData?.({...edge.getData?.(), _fromUserConnection: true});
		this._emitAddConnectionPatch(
			viewIndex ?? this._currentViewIndex ?? 0,
			connectionId,
			sourceNodeId,
			targetNodeId,
			relationshipRefId,
			RELATIONSHIP_ASSOCIATION,
			bendpoints || []
		);
		return true;
	}

	/**
	 * Отправить патч addConnection (создание связи создаётся через режим/кнопку, не через edge:connected).
	 */
	_emitAddConnectionPatch(
		viewIndex,
		connectionId,
		sourceId,
		targetId,
		relationshipRefId,
		relationshipType,
		bendpoints
	) {
		if (typeof this._onPatchCallback !== 'function') return;
		this._onPatchCallback({
			patches: [
				{
					op: 'addConnection',
					viewIndex: viewIndex ?? 0,
					connection: {
						identifier: connectionId,
						source: sourceId,
						target: targetId,
						relationshipRef: relationshipRefId,
						type: relationshipType,
						bendpoints: bendpoints || [],
					},
				},
			],
		});
	}

	/**
	 * Подписка на контекстное меню (правый клик по ячейке или по пустой доске).
	 * @param {function(Object): void} onContextMenu — вызывается с { type: 'connection'|'node'|'blank', cellId?, viewElement?, x, y, ... }
	 * @returns {function(): void} отписка
	 */
	setupContextMenu(onContextMenu) {
		if (typeof onContextMenu !== 'function') return () => {};
		const graph = this.getGraph();
		if (!graph) return () => {};

		const getLocalPoint = (clientX, clientY) => {
			const cx = clientX ?? 0;
			const cy = clientY ?? 0;
			const p =
				typeof graph.clientToLocal === 'function'
					? graph.clientToLocal(cx, cy)
					: graph.coord?.clientToLocalPoint?.(cx, cy);
			if (p == null) return {x: 0, y: 0};
			return {x: Number(p.x) || 0, y: Number(p.y) || 0};
		};

		const getNodeUnderPoint = point => {
			const nodes = graph.getNodes?.() ?? [];
			for (const node of nodes) {
				const bbox = node.getBBox?.();
				if (!bbox) continue;
				const w = bbox.width ?? 0;
				const h = bbox.height ?? 0;
				if (
					point.x >= bbox.x &&
					point.x <= bbox.x + w &&
					point.y >= bbox.y &&
					point.y <= bbox.y + h
				) {
					return node;
				}
			}
			return null;
		};

		const cellHandler = args => {
			const e = args?.e;
			if (e && e.preventDefault) e.preventDefault();
			const view = args?.view ?? args?.cellView;
			const cell = view?.cell ?? args?.cell;
			if (!cell) return;
			const cellId = cell.id;
			const viewElement = this._findViewElementById(cellId);
			const x = e?.clientX ?? 0;
			const y = e?.clientY ?? 0;

			if (cell.isEdge && cell.isEdge()) {
				const relationshipType = viewElement
					? this._getRelationshipType(viewElement)
					: null;
				const accessType = viewElement
					? this._getRelationshipAccessType(viewElement)
					: null;
				onContextMenu({
					type: 'connection',
					cellId,
					viewElement,
					x,
					y,
					relationshipType,
					accessType,
				});
			} else {
				onContextMenu({
					type: 'node',
					cellId,
					viewElement,
					x,
					y,
				});
			}
		};

		const blankHandler = e => {
			const local = getLocalPoint(e.clientX, e.clientY);
			if (getNodeUnderPoint(local)) return;
			const edgeViews = graph.getEdges?.() ?? [];
			for (const edge of edgeViews) {
				const bbox = edge.getBBox?.();
				if (!bbox) continue;
				if (
					local.x >= bbox.x &&
					local.x <= bbox.x + (bbox.width ?? 0) &&
					local.y >= bbox.y &&
					local.y <= bbox.y + (bbox.height ?? 0)
				) {
					return;
				}
			}
			e.preventDefault();
			onContextMenu({type: 'blank', x: e.clientX, y: e.clientY});
		};

		graph.on('cell:contextmenu', cellHandler);
		const container = graph.container;
		if (container && typeof container.addEventListener === 'function') {
			container.addEventListener('contextmenu', blankHandler);
		}
		return () => {
			graph.off('cell:contextmenu', cellHandler);
			if (container && typeof container.removeEventListener === 'function') {
				container.removeEventListener('contextmenu', blankHandler);
			}
		};
	}

	/**
	 * Проверка по правилам ArchiMate: допустимо ли соединение между двумя view-нодами.
	 * @param {Object} sourceViewNode
	 * @param {Object} targetViewNode
	 * @returns {boolean}
	 */
	_canConnectViewNodes(sourceViewNode, targetViewNode) {
		const sourceType =
			sourceViewNode?.elementRef?.type ??
			sourceViewNode?.$attrs?.elementRef?.type ??
			sourceViewNode?.type ??
			'Element';
		const targetType =
			targetViewNode?.elementRef?.type ??
			targetViewNode?.$attrs?.elementRef?.type ??
			targetViewNode?.type ??
			'Element';
		if (isCanvasOnlyNodeType(sourceType) || isCanvasOnlyNodeType(targetType)) return false;
		const sourceNodeType = nodeTypeRegistry.get(sourceType);
		if (!sourceNodeType) return false;
		return sourceNodeType.canConnectTo(targetType);
	}

	/**
	 * Допустим ли выбранный тип связи для пары источник→цель (по правилам ArchiMate).
	 * @param {Object} sourceViewNode
	 * @param {Object} targetViewNode
	 * @param {string} relationshipType
	 * @returns {boolean}
	 */
	_isRelationshipTypeAllowedForConnection(sourceViewNode, targetViewNode, relationshipType) {
		if (!relationshipType) return false;
		const sourceType =
			sourceViewNode?.elementRef?.type ??
			sourceViewNode?.$attrs?.elementRef?.type ??
			sourceViewNode?.type ??
			'Element';
		const targetType =
			targetViewNode?.elementRef?.type ??
			targetViewNode?.$attrs?.elementRef?.type ??
			targetViewNode?.type ??
			'Element';
		const sourceNodeType = nodeTypeRegistry.get(sourceType);
		if (!sourceNodeType) return false;
		const allowed = sourceNodeType.getAllowedRelationshipTypes?.(targetType) ?? [];
		if (allowed.length === 0) return true;
		return allowed.some(e => e.type === relationshipType && !e.reverse);
	}

	/**
	 * Валидатор для X6 connecting: разрешать соединение только если по правилам ArchiMate
	 * (getRelationshipsAllowed) пара source→target допустима.
	 * Вызывается при перетаскивании связи на узел до edge:connected.
	 * @param {Object} args — аргументы X6 (sourceCell, targetCell или sourceView, targetView)
	 * @returns {boolean}
	 */
	_validateConnectionForGraph(args) {
		const sourceCell = args?.sourceCell ?? args?.sourceView?.cell;
		const targetCell = args?.targetCell ?? args?.targetView?.cell;
		if (!sourceCell || !targetCell) return false;
		const getId = cell => cell?.id ?? cell?.getId?.() ?? cell?.getData?.()?.id;
		const sourceId = getId(sourceCell);
		const targetId = getId(targetCell);
		if (!sourceId || !targetId) return false;
		const srcNode = this._findViewElementById(sourceId);
		const tgtNode = this._findViewElementById(targetId);
		if (!srcNode || !tgtNode) return false;
		return this._canConnectViewNodes(srcNode, tgtNode);
	}

	/**
	 * Список допустимых типов связи для данной связи (по source/target).
	 * @returns {Array<{type: string, reverse?: boolean, label: string}>}
	 */
	getConnectionAllowedTypes(connectionViewId) {
		const viewElement = this._findViewElementById(connectionViewId);
		if (!viewElement || !viewElement.source || !viewElement.target) return [];
		const view = this._currentView;
		if (!view?.viewElements) return [];
		const nodeMap = {};
		const collect = arr => {
			for (const el of arr || []) {
				if (!el.waypoints && !el.relationshipRef && !(el.source && el.target)) {
					nodeMap[el.id ?? el.$attrs?.id] = el;
					if (el.viewElements) collect(el.viewElements);
				}
			}
		};
		collect(view.viewElements);
		const srcId = viewElement.source?.id ?? viewElement.source ?? viewElement.$attrs?.source;
		const tgtId = viewElement.target?.id ?? viewElement.target ?? viewElement.$attrs?.target;
		const sourceNode = nodeMap[srcId] || null;
		const targetNode = nodeMap[tgtId] || null;
		if (!sourceNode || !targetNode) return [];
		const sourceType =
			sourceNode.elementRef?.type ??
			sourceNode.$attrs?.elementRef?.type ??
			sourceNode.type ??
			'Element';
		const targetType =
			targetNode.elementRef?.type ??
			targetNode.$attrs?.elementRef?.type ??
			targetNode.type ??
			'Element';
		const sourceNodeType = nodeTypeRegistry.get(sourceType);
		if (!sourceNodeType) return [];
		return sourceNodeType.getAllowedRelationshipTypes(targetType);
	}

	/** Список типов связей для выбора при создании (источник известен, цель — при перетаскивании). */
	static get CONNECTION_TYPE_OPTIONS() {
		return [
			{type: RELATIONSHIP_ASSOCIATION, label: 'Association'},
			{type: RELATIONSHIP_ACCESS, label: 'Access'},
			{type: RELATIONSHIP_FLOW, label: 'Flow'},
			{type: RELATIONSHIP_SERVING, label: 'Serving'},
			{type: RELATIONSHIP_INFLUENCE, label: 'Influence'},
			{type: RELATIONSHIP_REALIZATION, label: 'Realization'},
			{type: RELATIONSHIP_AGGREGATION, label: 'Aggregation'},
			{type: RELATIONSHIP_COMPOSITION, label: 'Composition'},
			{type: RELATIONSHIP_ASSIGNMENT, label: 'Assignment'},
			{type: RELATIONSHIP_SPECIALIZATION, label: 'Specialization'},
			{type: RELATIONSHIP_TRIGGERING, label: 'Triggering'},
		];
	}

	/**
	 * Типы связей для выбора при создании связи от узла (для UI выбора типа).
	 * @returns {Array<{type: string, label: string}>}
	 */
	getConnectionTypesForSource() {
		return ModelerX6.CONNECTION_TYPE_OPTIONS;
	}

	/**
	 * Установить ожидаемый тип связи для следующего edge:connected от этого источника (после выбора типа в UI).
	 * @param {string} sourceNodeId
	 * @param {string} relationshipType
	 */
	setPendingConnection(sourceNodeId, relationshipType) {
		this._pendingConnection =
			sourceNodeId && relationshipType
				? {sourceId: sourceNodeId, type: relationshipType}
				: null;
	}

	/**
	 * Запуск перетаскивания связи от узла: создаётся временный edge, тянется за курсором, по отпусканию над узлом — создаётся связь.
	 * @param {string} sourceNodeId
	 * @param {string} relationshipType
	 * @param {{ clientX: number, clientY: number }} [initialClient] — начальная позиция (например при открытом пикере)
	 * @param {{ pointerId?: number }} [options] — pointerId для тачпада/тача (используются pointermove/pointerup вместо mouse)
	 */
	startConnectionDrag(sourceNodeId, relationshipType, initialClient = {}, options = {}) {
		const usePointer = options.pointerId != null;
		const pointerId = options.pointerId;
		const graph = this.getGraph();
		if (!graph) return;
		const sourceNode = graph.getCellById?.(sourceNodeId);
		if (!sourceNode || typeof sourceNode.isNode !== 'function' || !sourceNode.isNode()) return;
		const view = this._currentView;
		if (!view) return;
		const srcViewNode = this._findViewElementById(sourceNodeId);
		if (!srcViewNode) return;
		if (isCanvasOnlyNodeType(this._getType(srcViewNode))) return;

		this._pendingConnection = {sourceId: sourceNodeId, type: relationshipType};
		const VIEW_INDEX = this._currentViewIndex ?? 0;
		const tempEdgeId = `__temp-connect-${Date.now()}`;

		// В X6 координаты узлов и терминалов ребра — в системе local (меняется при zoom/pan).
		const getLocalPoint = (clientX, clientY) => {
			const cx = clientX ?? 0;
			const cy = clientY ?? 0;
			const p =
				typeof graph.clientToLocal === 'function'
					? graph.clientToLocal(cx, cy)
					: graph.coord?.clientToLocalPoint?.(cx, cy);
			if (p == null) return {x: 0, y: 0};
			return {x: Number(p.x) || 0, y: Number(p.y) || 0};
		};

		const sourceBBox = sourceNode.getBBox?.();
		const startPoint =
			sourceBBox && (sourceBBox.width != null || sourceBBox.x != null)
				? {
						x: sourceBBox.x + (sourceBBox.width ?? 0),
						y: sourceBBox.y + (sourceBBox.height ?? 0) / 2,
					}
				: getLocalPoint(initialClient.clientX, initialClient.clientY);

		const markerConfig = this._getEdgeMarkerConfigForRelationshipType(relationshipType, {});
		const lineAttrs = {
			stroke: '#000000',
			strokeWidth: 1,
			sourceMarker: markerConfig?.sourceMarker ?? '',
			targetMarker: markerConfig?.targetMarker ?? '',
			strokeDasharray: markerConfig?.strokeDasharray ?? '4 2',
		};

		let tempEdge;
		try {
			tempEdge = graph.addEdge({
				id: tempEdgeId,
				zIndex: 10000,
				source: {cell: sourceNodeId, anchor: 'orth', connectionPoint: 'boundary'},
				target: startPoint,
				attrs: {
					line: lineAttrs,
					/* wrap — невидимый hit-area ребра (см. X6 getEdgeMarkup); по умолчанию cursor:pointer, из‑за него не виден not-allowed с контейнера */
					wrap: {cursor: 'grabbing'},
				},
			});
		} catch (err) {
			this._pendingConnection = null;
			console.warn('startConnectionDrag: addEdge failed', err);
			return;
		}

		const getNodeUnderPoint = point => {
			let targetNode = null;
			let minArea = Infinity;
			const nodes = graph.getNodes?.() ?? [];
			for (const node of nodes) {
				if (node.id === sourceNodeId) continue;
				const bbox = node.getBBox?.();
				if (!bbox) continue;
				const w = bbox.width ?? 0;
				const h = bbox.height ?? 0;
				if (
					point.x >= bbox.x &&
					point.x <= bbox.x + w &&
					point.y >= bbox.y &&
					point.y <= bbox.y + h
				) {
					const area = w * h;
					if (area < minArea) {
						minArea = area;
						targetNode = node;
					}
				}
			}
			return targetNode;
		};

		const CONNECTION_TARGET_VALID_STROKE = '#2196f3';

		/** @type {{ nodeId: string, origStroke: *, origFilters: Record<string, string | null | undefined>, appliedMode: 'valid' | 'invalid' | null } | null} */
		let connectionDragHighlightState = null;

		const clearConnectionTargetHighlight = () => {
			if (!connectionDragHighlightState) return;
			const node = graph.getCellById?.(connectionDragHighlightState.nodeId);
			if (node) {
				try {
					node.attr?.('body/stroke', connectionDragHighlightState.origStroke);
					const origFilters = connectionDragHighlightState.origFilters || {};
					for (const sel of CONNECTION_DRAG_INVALID_FILTER_SELECTORS) {
						if (!Object.prototype.hasOwnProperty.call(origFilters, sel)) continue;
						const prev = origFilters[sel];
						node.attr?.(`${sel}/filter`, prev == null ? null : prev);
					}
				} catch {
					//
				}
			}
			connectionDragHighlightState = null;
		};

		const beginConnectionTargetHighlight = targetNode => {
			if (!targetNode?.id) return;
			if (connectionDragHighlightState?.nodeId === targetNode.id) return;
			clearConnectionTargetHighlight();
			const origStroke = targetNode.attr?.('body')?.stroke;
			const origFilters = {};
			for (const sel of CONNECTION_DRAG_INVALID_FILTER_SELECTORS) {
				try {
					const part = targetNode.attr?.(sel);
					if (part != null && typeof part === 'object') {
						origFilters[sel] = part.filter;
					}
				} catch {
					//
				}
			}
			connectionDragHighlightState = {
				nodeId: targetNode.id,
				origStroke,
				origFilters,
				appliedMode: null,
			};
		};

		const setConnectionTargetHighlightValid = targetNode => {
			if (!targetNode?.id) return;
			beginConnectionTargetHighlight(targetNode);
			const st = connectionDragHighlightState;
			if (!st || st.nodeId !== targetNode.id) return;
			if (st.appliedMode === 'valid') return;
			st.appliedMode = 'valid';
			try {
				const origFilters = st.origFilters || {};
				for (const sel of CONNECTION_DRAG_INVALID_FILTER_SELECTORS) {
					if (!Object.prototype.hasOwnProperty.call(origFilters, sel)) continue;
					const prev = origFilters[sel];
					targetNode.attr?.(`${sel}/filter`, prev == null ? null : prev);
				}
				targetNode.attr?.('body/stroke', CONNECTION_TARGET_VALID_STROKE);
			} catch {
				//
			}
		};

		const setConnectionTargetHighlightInvalid = targetNode => {
			if (!targetNode?.id) return;
			beginConnectionTargetHighlight(targetNode);
			const st = connectionDragHighlightState;
			if (!st || st.nodeId !== targetNode.id) return;
			if (st.appliedMode === 'invalid') return;
			st.appliedMode = 'invalid';
			const filterId = ensureInvalidConnectionTargetFilter(graph);
			const filterUrl = filterId ? `url(#${filterId})` : null;
			try {
				targetNode.attr?.('body/stroke', st.origStroke);
				if (filterUrl) {
					for (const sel of CONNECTION_DRAG_INVALID_FILTER_SELECTORS) {
						try {
							if (targetNode.attr?.(sel) != null) {
								targetNode.attr?.(`${sel}/filter`, filterUrl);
							}
						} catch {
							//
						}
					}
				}
			} catch {
				//
			}
		};

		/** Курсор при перетаскивании связи: контейнер + path «wrap» у временного ребра (поверх узлов; иначе перехватывает pointer из X6). */
		const setConnectionDragCursor = cursor => {
			const el = this._container ?? graph?.container;
			if (el?.style) {
				if (cursor) el.style.setProperty('cursor', cursor, 'important');
				else el.style.removeProperty('cursor');
			}
			const svg = el?.querySelector?.('svg');
			if (svg?.style) {
				if (cursor) svg.style.setProperty('cursor', cursor, 'important');
				else svg.style.removeProperty('cursor');
			}
			try {
				if (tempEdge) {
					tempEdge.attr('wrap/cursor', cursor || 'default');
				}
			} catch {
				//
			}
		};

		const applyMove = (clientX, clientY) => {
			const p = getLocalPoint(clientX, clientY);
			tempEdge.setTarget?.(p);
			const nodeUnder = getNodeUnderPoint(p);
			const tgtView = nodeUnder ? this._findViewElementById(nodeUnder.id) : null;
			const canConnect = Boolean(
				nodeUnder &&
				tgtView &&
				this._canConnectViewNodes(srcViewNode, tgtView) &&
				this._isRelationshipTypeAllowedForConnection(srcViewNode, tgtView, relationshipType)
			);
			if (canConnect) setConnectionTargetHighlightValid(nodeUnder);
			else if (nodeUnder) setConnectionTargetHighlightInvalid(nodeUnder);
			else clearConnectionTargetHighlight();
			const cursor = nodeUnder && tgtView && !canConnect ? 'not-allowed' : 'grabbing';
			setConnectionDragCursor(cursor);
		};
		const finishUp = (clientX, clientY) => {
			const point = getLocalPoint(clientX, clientY);
			const targetNode = getNodeUnderPoint(point);

			clearConnectionTargetHighlight();
			graph.removeCell(tempEdge);
			this._pendingConnection = null;
			setConnectionDragCursor(null);

			if (targetNode) {
				const tgtViewNode = this._findViewElementById(targetNode.id);
				if (tgtViewNode) {
					const structuralOk = this._canConnectViewNodes(srcViewNode, tgtViewNode);
					const relOk = this._isRelationshipTypeAllowedForConnection(
						srcViewNode,
						tgtViewNode,
						relationshipType
					);
					if (structuralOk && relOk) {
						this._pendingConnection = {sourceId: sourceNodeId, type: relationshipType};
						const result = connectionModel.createConnection(
							this,
							srcViewNode,
							tgtViewNode,
							relationshipType
						);
						this._pendingConnection = null;
						if (result) {
							const {connectionId, relationshipRefId, bendpoints} = result;
							const edge = this._edgeFactory.createAndAdd({
								id: connectionId,
								source: sourceNodeId,
								target: targetNode.id,
								vertices: [],
								relationshipType,
								lineColor: '#000000',
							});
							if (edge) {
								const prev =
									typeof edge.getData === 'function' ? edge.getData() : {};
								edge.setData?.({
									...(prev && typeof prev === 'object' ? prev : {}),
									_fromUserConnection: true,
								});
							}
							applyViewpointDimming(this);
							this._emitAddConnectionPatch(
								VIEW_INDEX,
								connectionId,
								sourceNodeId,
								targetNode.id,
								relationshipRefId,
								relationshipType,
								bendpoints || []
							);
						}
					} else {
						graph.trigger?.('connection:invalidDrop', {
							sourceNodeId,
							targetNodeId: targetNode.id,
							relationshipType,
							structuralBlock: !structuralOk,
						});
					}
				}
			}
		};

		const onPointerMove = e => {
			if (e.pointerId !== pointerId) return;
			applyMove(e.clientX, e.clientY);
		};
		const onPointerUp = e => {
			if (e.pointerId !== pointerId) return;
			document.removeEventListener('pointermove', onPointerMove, true);
			document.removeEventListener('pointerup', onPointerUp, true);
			this._connectionDragCleanup = null;
			finishUp(e.clientX, e.clientY);
		};
		const onMove = e => applyMove(e.clientX, e.clientY);
		const onUp = e => {
			document.removeEventListener('mousemove', onMove, true);
			document.removeEventListener('mouseup', onUp, true);
			this._connectionDragCleanup = null;
			finishUp(e.clientX, e.clientY);
		};

		if (usePointer) {
			document.addEventListener('pointermove', onPointerMove, true);
			document.addEventListener('pointerup', onPointerUp, true);
		} else {
			document.addEventListener('mousemove', onMove, true);
			document.addEventListener('mouseup', onUp, true);
		}
		this._connectionDragCleanup = () => {
			if (usePointer) {
				document.removeEventListener('pointermove', onPointerMove, true);
				document.removeEventListener('pointerup', onPointerUp, true);
			} else {
				document.removeEventListener('mousemove', onMove, true);
				document.removeEventListener('mouseup', onUp, true);
			}
			clearConnectionTargetHighlight();
			setConnectionDragCursor(null);
			try {
				graph.removeCell?.(tempEdge);
			} catch {
				//
			}
			this._pendingConnection = null;
			this._connectionDragCleanup = null;
		};
	}

	/**
	 * Обновить тип связи и перерисовать edge.
	 * @param {string} connectionViewId
	 * @param {string} newType
	 * @param {{ reverse?: boolean }} [options] — при reverse: true меняет source/target местами
	 */
	updateRelationshipType(connectionViewId, newType, options = {}) {
		const viewElement = this._findViewElementById(connectionViewId);
		if (!viewElement?.relationshipRef) return;
		const rel = viewElement.relationshipRef;
		if (rel.set) rel.set('type', newType);
		else rel.type = newType;

		if (options.reverse) {
			const swp = viewElement.source;
			viewElement.source = viewElement.target;
			viewElement.target = swp;
			const relSource = rel.source;
			rel.source = rel.target;
			rel.target = relSource;
			if (rel.set) {
				rel.set('source', rel.source);
				rel.set('target', rel.target);
			}
			const wps =
				viewElement.waypointsNode?.waypoints || viewElement.waypoints?.waypoint || [];
			const list = Array.isArray(wps) ? [...wps] : [wps].filter(Boolean);
			if (list.length > 0) {
				list.reverse();
				viewElement.waypointsNode = viewElement.waypointsNode || {};
				viewElement.waypointsNode.waypoints = list;
			}

			const graph = this.getGraph();
			const edge = graph?.getCellById?.(connectionViewId);
			if (
				edge &&
				typeof edge.setSource === 'function' &&
				typeof edge.setTarget === 'function'
			) {
				const src = edge.getSource?.()?.cell ?? edge.getSourceCellId?.();
				const tgt = edge.getTarget?.()?.cell ?? edge.getTargetCellId?.();
				const verts = edge.getVertices?.() ?? [];
				this._updatingConnectionFromModel = connectionViewId;

				try {
					edge.setSource({cell: tgt, anchor: 'orth', connectionPoint: 'boundary'});
					edge.setTarget({cell: src, anchor: 'orth', connectionPoint: 'boundary'});

					if (verts.length) edge.setVertices?.(verts.reverse());
				} finally {
					this._updatingConnectionFromModel = null;
				}
			}
		}

		const accessType = this._getRelationshipAccessType(viewElement);

		// Нормализованный тип для стилей (edgeConfig ожидает 'Flow', а не 'archimate:Flow')
		const normalizedType = this._normalizeType ? this._normalizeType(newType) : newType;

		const graph = this.getGraph();
		const edge = graph?.getCellById?.(connectionViewId);
		if (edge) {
			this._edgeFactory.refreshMarkers(edge, normalizedType, accessType);
		}

		this.getGraph()?.emit?.('connection:relationship:updated', {
			connectionViewId,
			relationshipType: newType,
			accessType: accessType ?? undefined,
		});
	}

	/**
	 * Обновить accessType для связи Access.
	 * @param {string} connectionViewId
	 * @param {string} accessType — Read | Write | ReadWrite
	 */
	updateRelationshipAccessType(connectionViewId, accessType) {
		const viewElement = this._findViewElementById(connectionViewId);
		if (!viewElement?.relationshipRef) return;
		const rel = viewElement.relationshipRef;
		if (rel.set) rel.set('accessType', accessType);
		else rel.accessType = accessType;
		const relationshipType = this._getRelationshipType(viewElement);
		const edge = this.getGraph()?.getCellById?.(connectionViewId);
		if (edge) this._edgeFactory.refreshMarkers(edge, relationshipType, accessType);
		this.getGraph()?.emit?.('connection:relationship:updated', {
			connectionViewId,
			relationshipType,
			accessType,
		});
	}

	/**
	 * Удалить связь с диаграммы (вызов из контекстного меню).
	 * Патч removeConnection уходит из edgeRemovedPlugin по событию edge:removed.
	 */
	removeConnectionFromView(connectionViewId) {
		this._edgeFactory.remove(connectionViewId);
	}

	/**
	 * Удалить узел с диаграммы вместе со всеми вложенными узлами (viewElements).
	 * Сначала удаляются рёбра, инцидентные любому из удаляемых узлов, затем узлы снизу вверх.
	 * Патчи removeNode/removeConnection уходят из плагинов по событиям графа.
	 */
	removeNodeFromView(nodeViewId) {
		const viewElement = this._findViewElementById(nodeViewId);
		if (!viewElement) return;
		const graph = this.getGraph();
		if (!graph) return;
		const postOrderIds = viewElements.collectDescendantNodeViewIdsPostOrder(viewElement);
		if (postOrderIds.length === 0) return;
		const idSet = new Set(postOrderIds);
		const edges = graph.getEdges?.() ?? [];
		const edgeIdsToRemove = new Set();
		for (const edge of edges) {
			const sid =
				edge.getSourceCellId?.() ?? edge.getSource?.()?.cell ?? edge.getSourceCell?.()?.id;
			const tid =
				edge.getTargetCellId?.() ?? edge.getTarget?.()?.cell ?? edge.getTargetCell?.()?.id;
			if (idSet.has(sid) || idSet.has(tid)) edgeIdsToRemove.add(edge.id);
		}
		for (const eid of edgeIdsToRemove) {
			this._edgeFactory.remove(eid);
		}
		for (const nid of postOrderIds) {
			this._nodeFactory.remove(nid);
		}
	}

	/**
	 * Вынести ячейку (узел или связь) вперёд по порядку отрисовки. Дочерние элементы поднимаются вместе с родителем.
	 * После поднятия узла все рёбра снова поднимаются поверх узлов.
	 */
	bringCellToFront(cellId) {
		const graph = this.getGraph();
		if (!graph) return;
		const cell = graph.getCellById?.(cellId) ?? graph.model?.getCellById?.(cellId);
		if (!cell) return;
		const setZ = (c, z) => {
			if (c && typeof c.setZIndex === 'function') c.setZIndex(z);
		};
		const getAllCells = () => {
			if (typeof graph.getCells === 'function') return graph.getCells();
			const model = graph.model ?? graph.getModel?.();
			if (model && typeof model.getCells === 'function') return model.getCells();
			const nodes = graph.getNodes?.() ?? [];
			const edges = graph.getEdges?.() ?? [];
			return [...nodes, ...edges];
		};
		const getMaxZ = () => {
			const cells = getAllCells();
			let max = 0;
			cells.forEach(c => {
				const z = typeof c.getZIndex === 'function' ? c.getZIndex() : c.zIndex;
				if (typeof z === 'number' && z > max) max = z;
			});
			return max;
		};
		const nextZ = getMaxZ() + 1;
		setZ(cell, nextZ);
		if (cell.getChildren && typeof cell.getChildren === 'function') {
			const children = cell.getChildren?.() ?? [];
			children.forEach((child, i) => setZ(child, nextZ + 1 + i));
		}
		const edges = graph.getEdges?.() ?? [];
		edges.forEach(edge => {
			const ez = getMaxZ() + 1;
			setZ(edge, ez);
		});
	}

	/**
	 * Вынести ячейку (узел или связь) назад по порядку отрисовки. Дочерние элементы остаются поверх родителя.
	 */
	sendCellToBack(cellId) {
		const graph = this.getGraph();
		if (!graph) return;
		const cell = graph.getCellById?.(cellId) ?? graph.model?.getCellById?.(cellId);
		if (!cell) return;
		const setZ = (c, z) => {
			if (c && typeof c.setZIndex === 'function') c.setZIndex(z);
		};
		const getAllCellsForBack = () => {
			if (typeof graph.getCells === 'function') return graph.getCells();
			const model = graph.model ?? graph.getModel?.();
			if (model && typeof model.getCells === 'function') return model.getCells();
			const nodes = graph.getNodes?.() ?? [];
			const edges = graph.getEdges?.() ?? [];
			return [...nodes, ...edges];
		};
		const getMinZ = () => {
			const cells = getAllCellsForBack();
			let min = 0;
			cells.forEach(c => {
				const z = typeof c.getZIndex === 'function' ? c.getZIndex() : c.zIndex;
				if (typeof z === 'number' && z < min) min = z;
			});
			return min;
		};
		const baseZ = getMinZ() - 1;
		setZ(cell, baseZ);
		if (cell.getChildren && typeof cell.getChildren === 'function') {
			const children = cell.getChildren?.() ?? [];
			children.forEach((child, i) => setZ(child, baseZ + 1 + i));
		}
	}

	/**
	 * Удаляет edge с графа и связь из модели (для вызова из applyModelPatch и т.п.).
	 */
	_removeConnectionFromViewAndGraph(connectionViewId) {
		this._removingConnectionId = connectionViewId;
		try {
			this._edgeFactory.remove(connectionViewId);
		} finally {
			this._removingConnectionId = null;
		}
		connectionModel.removeConnection(this, connectionViewId);
	}

	_findViewElementById(id) {
		return viewElements.findViewElementById(this, id);
	}

	/**
	 * Переносит view element в другого родителя (для вложенности Group/Grouping).
	 * Удаляет из текущего места в viewElements и добавляет в viewElements нового родителя или в корень.
	 * @param {string} childId — id узла (view element)
	 * @param {string|null} newParentId — id нового родителя или null для корня
	 */
	_moveViewElementToParent(childId, newParentId) {
		viewElements.moveViewElementToParent(this, childId, newParentId);
	}

	/**
	 * Находит viewElement по conceptId (elementRef.id или relationshipRef.id).
	 * @param {string} conceptId
	 * @param {boolean} isRelationship
	 * @returns {{ id: string } | null}
	 */
	_findViewElementByConceptId(conceptId, isRelationship) {
		return viewElements.findViewElementByConceptId(this, conceptId, isRelationship);
	}

	/**
	 * Удаляет элемент или связь с текущей диаграммы (только из view, не из модели).
	 * @param {string} conceptId
	 * @param {boolean} isRelationship
	 * @returns {boolean} true если удалено
	 */
	removeConceptFromView(conceptId, isRelationship) {
		return viewElements.removeConceptFromView(this, conceptId, isRelationship);
	}

	/**
	 * Добавляет элемент или связь каталога на текущую диаграмму.
	 * После добавления выделяет концепт и центрирует на нём viewport.
	 * @param {string} conceptId — id элемента или связи
	 * @param {boolean} [isRelationship]
	 * @param {{type?: string, name?: string, source?: string, target?: string, accessType?: string}} [catalogHint]
	 */
	addConceptToView(conceptId, isRelationship, catalogHint) {
		return viewElements.addConceptToView(this, conceptId, isRelationship, catalogHint);
	}

	collectElementRefsOnCurrentView() {
		return viewElements.collectElementRefsOnCurrentView(this);
	}

	placeCatalogRelationshipOnView(dto) {
		return viewElements.placeCatalogRelationshipOnView(this, dto);
	}

	/**
	 * Выделяет элемент или связь на текущей диаграмме по conceptId.
	 * Центрирует видимую область на выделенном элементе.
	 * @param {string} conceptId
	 * @param {boolean} isRelationship
	 */
	selectConcept(conceptId, isRelationship) {
		viewElements.selectConcept(this, conceptId, isRelationship);
	}

	/**
	 * Выделить элемент диаграммы по canvas id (identifier из модели / ответа AI) и прокрутить к нему.
	 * @param {string} canvasId
	 * @returns {boolean}
	 */
	focusCanvasElementById(canvasId) {
		return viewElements.focusViewElementByCanvasId(this, canvasId);
	}

	getViews() {
		const model = this._model;
		const views =
			model && model.views && model.views.diagrams && model.views.diagrams.viewsList;
		return views || [];
	}

	getActiveViewId() {
		const v = this._currentView;
		return v?.id ?? v?.$attrs?.id ?? null;
	}

	getActiveViewIndex() {
		return this._currentViewIndex ?? 0;
	}

	/** @returns {string|null} стандартный viewpoint (ViewpointsEnum) для текущей диаграммы */
	getActiveViewViewpoint() {
		const v = this._currentView;
		if (!v) return null;
		const vp = v.viewpoint ?? v.$attrs?.viewpoint;
		if (vp == null || String(vp).trim() === '') return null;
		return String(vp).trim();
	}

	/**
	 * Задать viewpoint текущей диаграммы (только enum из XSD; без viewpointRef).
	 * @param {string|null|undefined} value — null или пусто сбрасывают
	 */
	setActiveViewViewpoint(value) {
		const v = this._currentView;
		if (!v) return;
		const next = value != null && String(value).trim() !== '' ? String(value).trim() : null;
		if (typeof v.set === 'function') v.set('viewpoint', next);
		else v.viewpoint = next;
		applyViewpointDimming(this);
	}

	getModel() {
		return this._model || null;
	}

	/**
	 * Создать новый view (диаграмму) в модели и открыть его.
	 * @param {string} name — отображаемое имя новой диаграммы
	 * @returns {Promise<{view: object, warnings: Array}>}
	 */
	createView(name) {
		const model = this._model;
		const moddle = this._moddle;
		if (!model?.views?.diagrams?.viewsList) {
			return Promise.reject(new Error('No model loaded'));
		}
		const viewsList = model.views.diagrams.viewsList;
		const newView = moddle.create('archimate:View', {
			name: name || 'Новая диаграмма',
			documentation: '',
		});
		newView.id = moddle.ids.nextPrefixed('id-', newView);
		newView.viewElements = [];
		viewsList.push(newView);
		return this.openView(newView.id).then(result => ({
			view: newView,
			warnings: result?.warnings ?? [],
		}));
	}

	/**
	 * Удалить view. Последнюю удалить нельзя.
	 * @param {string} viewId
	 * @returns {Promise<void>}
	 */
	deleteView(viewId) {
		const model = this._model;
		if (!model?.views?.diagrams?.viewsList) {
			return Promise.reject(new Error('No model loaded'));
		}
		const viewsList = model.views.diagrams.viewsList;
		if (viewsList.length <= 1) {
			return Promise.reject(new Error('Cannot delete the last view'));
		}
		const idx = viewsList.findIndex(v => (v?.id ?? v?.$attrs?.id) === viewId);
		if (idx < 0) return Promise.reject(new Error('View not found'));
		viewsList.splice(idx, 1);
		const nextView = viewsList[idx] || viewsList[idx - 1] || viewsList[0];
		return this.openView(nextView?.id ?? nextView?.$attrs?.id);
	}

	/**
	 * Правки каталога с другой доски проекта: те же концепты, другие view-id.
	 * @param {Array<{op: string, elementIdentifier?: string, relationshipIdentifier?: string, name?: string, properties?: object, relationshipType?: string, relationshipName?: string, label?: string}>} changes
	 */
	applyCatalogChange(changes) {
		if (!Array.isArray(changes) || !changes.length) return;
		const getElementId = o => o?.id ?? o?.$attrs?.id;
		const findCatalogElement = identifier => {
			const elementsNode = this._model?.elementsNode || this._model?.elements;
			const arr = elementsNode?.element || elementsNode?.elements;
			const elements = Array.isArray(arr) ? arr : [];
			return elements.find(e => getElementId(e) === identifier) ?? null;
		};
		const findCatalogRelationship = identifier => {
			const relsNode = this._model?.relationshipsNode || this._model?.relationships;
			const arr = relsNode?.relationship || relsNode?.relationships;
			const rels = Array.isArray(arr) ? arr : [];
			return rels.find(r => getElementId(r) === identifier) ?? null;
		};
		const refreshCells = (conceptId, isRelationship, extra) => {
			const viewEls = viewElements.findAllViewElementsByConceptId(this, conceptId, isRelationship);
			const graph = this.getGraph();
			for (const viewEl of viewEls) {
				const id = getElementId(viewEl);
				if (!id) continue;
				const cell = graph?.getCellById?.(id);
				if (!cell) continue;
				if (!isRelationship && typeof cell.isNode === 'function' && cell.isNode()) {
					this._nodeFactory?.update(cell, extra?.label != null ? {label: extra.label} : undefined);
				} else if (isRelationship) {
					this._edgeFactory?.update?.(cell);
				}
			}
		};
		for (const change of changes) {
			if (!change?.op) continue;
			if (change.op === 'removeElementFromModel' && change.elementIdentifier) {
				this.applyModelPatch({
					patches: [{op: 'removeElementFromModel', identifier: change.elementIdentifier}],
				});
				continue;
			}
			if (change.op === 'removeRelationshipFromModel' && change.relationshipIdentifier) {
				this.applyModelPatch({
					patches: [
						{op: 'removeRelationshipFromModel', identifier: change.relationshipIdentifier},
					],
				});
				continue;
			}
			if (
				(change.op === 'updateElementLabel' || change.op === 'updateElementProperties') &&
				change.elementIdentifier
			) {
				const elem = findCatalogElement(change.elementIdentifier);
				if (!elem) continue;
				const name = change.name ?? change.properties?.name;
				if (name != null) {
					if (elem.set) elem.set('name', name);
					else elem.name = name;
				}
				const docs = change.properties?.documentation;
				if (docs != null) {
					if (elem.set) elem.set('documentation', docs);
					else elem.documentation = docs;
				}
				const nextType = change.properties?.type;
				if (nextType != null && String(nextType).trim() !== '') {
					if (elem.set) elem.set('type', String(nextType).trim());
					else elem.type = String(nextType).trim();
				}
				refreshCells(change.elementIdentifier, false, {
					label: name != null ? String(name) : undefined,
				});
				continue;
			}
			if (
				(change.op === 'updateRelationship' ||
					change.op === 'updateConnectionLabel' ||
					change.op === 'updateElementProperties') &&
				change.relationshipIdentifier
			) {
				const rel = findCatalogRelationship(change.relationshipIdentifier);
				if (!rel) continue;
				const relName =
					change.relationshipName ?? change.label ?? change.properties?.name ?? change.name;
				if (relName != null) {
					if (rel.set) rel.set('name', relName);
					else rel.name = relName;
				}
				const relType = change.relationshipType ?? change.properties?.type;
				if (relType != null) {
					if (rel.set) rel.set('type', relType);
					else rel.type = relType;
				}
				refreshCells(change.relationshipIdentifier, true);
			}
		}
	}

	/**
	 * Применить патчи от WebSocket (model-level + элементы других view).
	 * @param {{patches: Array}} patch
	 */
	applyModelPatch(patch) {
		if (!patch?.patches?.length) return;
		const model = this._model;
		const moddle = this._moddle;
		if (!model || !moddle) return;
		const diagrams = model?.views?.diagrams;
		const viewsList = diagrams?.viewsList;
		if (!viewsList) return;

		const currentViewIndex = this._currentViewIndex ?? 0;
		const getViewId = v => v?.id ?? v?.$attrs?.id;
		const getElementId = o => o?.id ?? o?.$attrs?.id;

		const getTargetViewElements = viewIdx => {
			const v = viewsList[viewIdx];
			if (!v?.viewElements) return [];
			return Array.isArray(v.viewElements) ? v.viewElements : [v.viewElements];
		};

		const findBy = (viewIdx, id) => {
			const elements = getTargetViewElements(viewIdx);
			const search = arr => {
				for (const o of arr) {
					if (getElementId(o) === id) return o;
					const ch = o.viewElements || [];
					if (ch.length) {
						const found = search(ch);
						if (found) return found;
					}
				}
				return null;
			};
			return search(elements);
		};

		const removeFromParent = (viewIdx, id) => {
			const elements = getTargetViewElements(viewIdx);
			const search = arr => {
				for (let i = 0; i < arr.length; i++) {
					if (getElementId(arr[i]) === id) {
						arr.splice(i, 1);
						return true;
					}
					const ch = arr[i].viewElements || [];
					if (ch.length && search(ch)) return true;
				}
				return false;
			};
			return search(elements);
		};

		let needsRerender = false;

		for (const op of patch.patches) {
			const viewIndex = op.viewIndex != null ? op.viewIndex : 0;
			const isOtherView = viewIndex !== currentViewIndex;

			if (op.op === 'addView' && op.viewIdentifier && op.viewName) {
				if (viewsList.some(v => getViewId(v) === op.viewIdentifier)) continue;
				const newView = moddle.create('archimate:View', {
					id: op.viewIdentifier,
					name: op.viewName,
				});
				if (moddle.ids?.claim) moddle.ids.claim(op.viewIdentifier, newView);
				if (newView.$parent !== undefined) newView.$parent = diagrams;
				viewsList.push(newView);
			} else if (op.op === 'updateViewName' && op.viewIdentifier && op.viewName) {
				const v = viewsList.find(item => getViewId(item) === op.viewIdentifier);
				if (v) {
					if (v.set) v.set('name', op.viewName);
					else v.name = op.viewName;
				}
			} else if (op.op === 'updateViewpoint' && op.viewIdentifier) {
				const v = viewsList.find(item => getViewId(item) === op.viewIdentifier);
				if (v) {
					const next =
						op.viewpoint != null && String(op.viewpoint).trim() !== ''
							? String(op.viewpoint).trim()
							: null;
					if (v.set) v.set('viewpoint', next);
					else v.viewpoint = next;
				}
			} else if (op.op === 'removeView' && op.viewIdentifier) {
				if (viewsList.length <= 1) continue;
				const idx = viewsList.findIndex(item => getViewId(item) === op.viewIdentifier);
				if (idx < 0) continue;
				viewsList.splice(idx, 1);
				const nextView = viewsList[idx] || viewsList[idx - 1] || viewsList[0];
				const nextId = nextView ? getViewId(nextView) : null;
				if (nextId) this.openView(nextId);
			} else if (op.op === 'removeElementFromModel' && op.identifier) {
				const elemId = op.identifier;
				const relsArr =
					model.relationshipsNode?.relationship ||
					model.relationships?.relationship ||
					[];
				const rels = Array.isArray(relsArr) ? relsArr : [relsArr].filter(Boolean);
				const relIdsToRemove = new Set();
				for (const r of rels) {
					const srcId = getElementId(r.source);
					const tgtId = getElementId(r.target);
					if (srcId === elemId || tgtId === elemId) {
						const rid = getElementId(r);
						if (rid) relIdsToRemove.add(rid);
					}
				}
				const elementsNode = model.elementsNode || model.elements;
				const elements = this._liveList(
					elementsNode,
					'element',
					elementsNode?.element || elementsNode?.elements
				);
				const elemIdx = elements.findIndex(e => getElementId(e) === elemId);
				if (elemIdx >= 0) elements.splice(elemIdx, 1);
				const relationshipsNode = model.relationshipsNode || model.relationships;
				const liveRels = this._liveList(
					relationshipsNode,
					'relationship',
					relationshipsNode?.relationship || rels
				);
				for (let i = liveRels.length - 1; i >= 0; i--) {
					if (relIdsToRemove.has(getElementId(liveRels[i]))) liveRels.splice(i, 1);
				}
				const viewElementIdsToRemove = new Set();
				const removeFromViewElements = (arr, pred) => {
					for (let i = arr.length - 1; i >= 0; i--) {
						const ch = arr[i].viewElements || [];
						if (ch.length) removeFromViewElements(ch, pred);
						if (pred(arr[i])) {
							const veId = getElementId(arr[i]);
							if (veId) viewElementIdsToRemove.add(veId);
							arr.splice(i, 1);
						}
					}
				};
				const pred = el => {
					const elRefId = getElementId(el.elementRef);
					const relRefId = getElementId(el.relationshipRef);
					return elRefId === elemId || (relRefId && relIdsToRemove.has(relRefId));
				};
				for (const v of viewsList) {
					const ve = this._liveList(v, 'viewElements', v.viewElements);
					if (ve.length) removeFromViewElements(ve, pred);
				}
				// Удаляем ячейки с канвы напрямую
				const graph = this.getGraph();
				if (graph && viewElementIdsToRemove.size) {
					for (const id of viewElementIdsToRemove) {
						const cell = graph.getCellById?.(id);
						if (!cell) continue;
						this._releaseCellChrome(cell);
						this._applyingPatch = true;
						try {
							graph.removeCell(cell);
						} finally {
							this._applyingPatch = false;
						}
					}
				}
			} else if (op.op === 'removeRelationshipFromModel' && op.identifier) {
				const relId = op.identifier;
				const relationshipsNode = model.relationshipsNode || model.relationships;
				const rels = this._liveList(
					relationshipsNode,
					'relationship',
					relationshipsNode?.relationship
				);
				const relIdx = rels.findIndex(r => getElementId(r) === relId);
				if (relIdx >= 0) rels.splice(relIdx, 1);
				const viewElementIdsToRemove = new Set();
				const removeFromViewElements = (arr, pred) => {
					for (let i = arr.length - 1; i >= 0; i--) {
						const ch = arr[i].viewElements || [];
						if (ch.length) removeFromViewElements(ch, pred);
						if (pred(arr[i])) {
							const veId = getElementId(arr[i]);
							if (veId) viewElementIdsToRemove.add(veId);
							arr.splice(i, 1);
						}
					}
				};
				const pred = el => getElementId(el.relationshipRef) === relId;
				for (const v of viewsList) {
					const ve = this._liveList(v, 'viewElements', v.viewElements);
					if (ve.length) removeFromViewElements(ve, pred);
				}
				// Удаляем ячейки связей с канвы напрямую
				const graph = this.getGraph();
				if (graph && viewElementIdsToRemove.size) {
					for (const id of viewElementIdsToRemove) {
						this._removingConnectionId = id;
						try {
							this._edgeFactory.remove(id);
						} finally {
							this._removingConnectionId = null;
						}
						connectionModel.removeConnection(this, id);
					}
				}
			} else if (viewsList[viewIndex]) {
				const targetView = viewsList[viewIndex];
				if (!targetView.viewElements) targetView.viewElements = [];
				// const viewElements = getTargetViewElements(viewIndex);

				if (op.op === 'updateNode' && op.identifier) {
					const node = findBy(viewIndex, op.identifier);
					if (node) {
						const hasBounds =
							op.x !== undefined ||
							op.y !== undefined ||
							op.w !== undefined ||
							op.h !== undefined;
						if (hasBounds) {
							const x = op.x ?? node.x ?? 0;
							const y = op.y ?? node.y ?? 0;
							const w = op.w ?? node.w ?? 120;
							const h = op.h ?? node.h ?? 60;
							node.x = x;
							node.y = y;
							node.w = w;
							node.h = h;
							if (node.bounds) {
								node.bounds.x = x;
								node.bounds.y = y;
								node.bounds.width = w;
								node.bounds.height = h;
							} else {
								node.bounds = {x, y, width: w, height: h};
							}
							if (!isOtherView && op.identifier !== this._draggingNodeId) {
								this._applyingPatch = true;
								try {
									const graph = this.getGraph();
									const cell = graph?.getCellById?.(op.identifier);
									if (cell?.setPosition) cell.setPosition(x, y);
									if (cell?.setSize) cell.setSize(w, h);
								} finally {
									this._applyingPatch = false;
								}
							}
						}
						// Стили внешнего вида (fillColor, lineColor)
						if (op.fillColor || op.lineColor) {
							if (!node.style) node.style = moddle.create('archimate:Style', {});
							const style = node.style;
							if (
								op.fillColor &&
								typeof op.fillColor === 'object' &&
								node.relationshipRef == null
							) {
								style.fillColor = moddle.create('archimate:FillColor', {
									r: op.fillColor.r ?? 255,
									g: op.fillColor.g ?? 255,
									b: op.fillColor.b ?? 255,
									a: op.fillColor.a ?? 100,
								});
							}
							if (op.lineColor && typeof op.lineColor === 'object') {
								style.lineColor = moddle.create('archimate:LineColor', {
									r: op.lineColor.r ?? 0,
									g: op.lineColor.g ?? 0,
									b: op.lineColor.b ?? 0,
									a: op.lineColor.a ?? 100,
								});
							}
							if (!isOtherView) {
								const graph = this.getGraph();
								const cell = graph?.getCellById?.(op.identifier);
								if (cell) {
									if (typeof cell.isNode === 'function' && cell.isNode()) {
										this._nodeFactory.update(cell);
									} else if (typeof cell.isEdge === 'function' && cell.isEdge()) {
										const relationshipType =
											renderView.getRelationshipType?.(this, node) ??
											'Association';
										const accessType = renderView.getRelationshipAccessType?.(
											this,
											node
										);
										const lineColorRgba = style.lineColor
											? toRgbaString(style.lineColor)
											: undefined;
										this._edgeFactory.update(cell, {
											lineColor: lineColorRgba,
											relationshipType,
											accessType,
										});
									}
								}
							}
						}
						if (op.includeParent) {
							const parentId =
								typeof op.parentNodeId === 'string' && op.parentNodeId
									? op.parentNodeId
									: null;
							this._moveViewElementToParent?.(op.identifier, parentId);
							if (!isOtherView) {
								const graph = this.getGraph();
								const cell = graph?.getCellById?.(op.identifier);
								if (cell) {
									this._applyingPatch = true;
									try {
										if (parentId) {
											const parent = graph.getCellById?.(parentId);
											if (parent && typeof parent.addChild === 'function') {
												parent.addChild(cell);
												this.bringCellToFront(parentId);
											}
										} else {
											const currentParent =
												typeof cell.getParent === 'function'
													? cell.getParent()
													: null;
											if (
												currentParent &&
												typeof currentParent.unembed === 'function'
											) {
												currentParent.unembed(cell);
											}
										}
									} finally {
										this._applyingPatch = false;
									}
								}
							}
						}
					}
				} else if (op.op === 'updateNodeStyles' && op.identifier) {
					const viewEl = findBy(viewIndex, op.identifier);
					if (viewEl && op.styles && typeof op.styles === 'object') {
						if (!viewEl.style) viewEl.style = moddle.create('archimate:Style', {});
						const setStyle = (key, val) => {
							if (viewEl.style.set) viewEl.style.set(key, val);
							else viewEl.style[key] = val;
						};
						if (op.styles.textAlign !== undefined) {
							setStyle(
								'textAlign',
								op.styles.textAlign === '' ? undefined : op.styles.textAlign
							);
						}
						if (op.styles.lineWidth !== undefined) {
							const w = Number(op.styles.lineWidth);
							setStyle('lineWidth', Number.isFinite(w) && w >= 0 ? w : undefined);
						}
						if (!isOtherView) {
							const graph = this.getGraph();
							const cell = graph?.getCellById?.(op.identifier);
							if (cell && typeof cell.isNode === 'function' && cell.isNode()) {
								this._nodeFactory.update(cell);
							}
						}
					}
				} else if (op.op === 'updateElementLabel' && op.identifier) {
					const viewEl = findBy(viewIndex, op.identifier);
					const newText = op.elementName ?? '';
					if (viewEl) {
						viewEl.label = newText;
						const elementRef = this._getElementRef(viewEl);
						if (elementRef && typeof elementRef.name !== 'undefined') {
							if (elementRef.set) elementRef.set('name', newText);
							else elementRef.name = newText;
						}
						if (!isOtherView) {
							const graph = this.getGraph();
							const cell = graph?.getCellById?.(op.identifier);
							if (cell && typeof cell.isNode === 'function' && cell.isNode()) {
								this._nodeFactory.update(cell, {label: newText});
							}
						}
					}
				} else if (op.op === 'removeNode' && op.identifier) {
					removeFromParent(viewIndex, op.identifier);
					if (!isOtherView) {
						const graph = this.getGraph();
						const cell = graph?.getCellById?.(op.identifier);
						if (cell) {
							this._releaseCellChrome(cell);
							this._applyingPatch = true;
							try {
								graph.removeCell(cell);
							} finally {
								this._applyingPatch = false;
							}
						}
					}
				} else if (op.op === 'removeConnection' && op.identifier) {
					this._removingConnectionId = op.identifier;
					try {
						this._removeConnectionFromViewAndGraph(op.identifier);
					} finally {
						this._removingConnectionId = null;
					}
				} else if (op.op === 'updateConnection' && op.identifier) {
					const conn = findBy(viewIndex, op.identifier);
					if (conn) {
						if (Array.isArray(op.bendpoints)) {
							if (op.bendpoints.length > 0) {
								const waypointEls = op.bendpoints.map(p =>
									moddle.create('archimate:Waypoint', {x: p.x, y: p.y})
								);
								const wp = moddle.create('archimate:Waypoints', {
									waypoints: waypointEls,
								});
								if (wp.waypoints) {
									for (const w of wp.waypoints) {
										if (w) w.$parent = wp;
									}
								}
								wp.$parent = conn;
								conn.waypointsNode = wp;
							} else {
								conn.waypointsNode = null;
							}
						}
						if (op.source != null && op.target != null) {
							const nodeMap = {};
							const collectNodes = arr => {
								for (const el of arr) {
									if (
										!el.waypoints &&
										!el.relationshipRef &&
										!(el.source && el.target)
									) {
										nodeMap[getElementId(el)] = el;
										if (el.viewElements) collectNodes(el.viewElements);
									}
								}
							};
							collectNodes(getTargetViewElements(viewIndex));
							const srcNode = nodeMap[op.source];
							const tgtNode = nodeMap[op.target];
							if (srcNode && tgtNode) {
								conn.source = srcNode;
								conn.target = tgtNode;
								if (conn.relationshipRef) {
									conn.relationshipRef.source =
										srcNode?.elementRef ?? srcNode?.$attrs?.elementRef;
									conn.relationshipRef.target =
										tgtNode?.elementRef ?? tgtNode?.$attrs?.elementRef;
									if (conn.relationshipRef.set) {
										conn.relationshipRef.set(
											'source',
											conn.relationshipRef.source
										);
										conn.relationshipRef.set(
											'target',
											conn.relationshipRef.target
										);
									}
								}
								if (!isOtherView) {
									const g = this.getGraph();
									const edge = g?.getCellById?.(op.identifier);
									if (edge?.setSource && edge?.setTarget) {
										edge.setSource({
											cell: op.source,
											anchor: 'orth',
											connectionPoint: 'boundary',
										});
										edge.setTarget({
											cell: op.target,
											anchor: 'orth',
											connectionPoint: 'boundary',
										});
									}
								}
							}
						}
						if (!isOtherView && Array.isArray(op.bendpoints)) {
							const g = this.getGraph();
							const edge = g?.getCellById?.(op.identifier);
							if (edge?.setVertices) {
								const vertices = op.bendpoints.map(p => ({
									x: p.x ?? 0,
									y: p.y ?? 0,
								}));
								this._updatingConnectionVerticesFromModel = op.identifier;
								edge.setVertices(vertices);
								setTimeout(() => {
									if (
										this._updatingConnectionVerticesFromModel === op.identifier
									) {
										this._updatingConnectionVerticesFromModel = null;
									}
								}, 400);
							}
						}
					}
				} else if (op.op === 'updateConnectionLabel' && op.identifier) {
					const conn = findBy(viewIndex, op.identifier);
					const labelText = op.label ?? '';
					if (conn) {
						conn.label = labelText;
						const rel = conn.relationshipRef;
						if (rel) {
							if (rel.set) rel.set('name', labelText);
							else rel.name = labelText;
						}
						if (!isOtherView) {
							const edge = this.getGraph()?.getCellById?.(op.identifier);
							if (edge) {
								this._applyingPatch = true;
								try {
									if ((edge.getLabels?.() ?? []).length > 0) edge.removeLabelAt?.(0);
									if (labelText) {
										edge.appendLabel?.({attrs: {text: {text: labelText}}});
									}
								} finally {
									this._applyingPatch = false;
								}
							}
						}
					}
				} else if (op.op === 'updateRelationship' && op.identifier) {
					const conn = findBy(viewIndex, op.identifier);
					if (conn?.relationshipRef) {
						if (op.relationshipType != null) {
							if (conn.relationshipRef.set)
								conn.relationshipRef.set('type', op.relationshipType);
							else conn.relationshipRef.type = op.relationshipType;
						}
						if (op.accessType != null) {
							if (conn.relationshipRef.set)
								conn.relationshipRef.set('accessType', op.accessType);
							else conn.relationshipRef.accessType = op.accessType;
						}
						if (!isOtherView) {
							const graph = this.getGraph();
							const edge = graph?.getCellById?.(op.identifier);
							const relType = conn.relationshipRef.type ?? op.relationshipType;
							const normalizedRelType = this._normalizeType
								? this._normalizeType(relType)
								: relType;
							const accessType = conn.relationshipRef.accessType ?? op.accessType;
							if (edge && this._edgeFactory) {
								this._edgeFactory.refreshMarkers(
									edge,
									normalizedRelType,
									accessType
								);
							}
						}
					}
				} else if (op.op === 'addNode' && op.node) {
					const n = op.node;
					const id = n.identifier ?? `id-node-${Date.now()}`;
					const canvasOnly = isCanvasOnlyNodeType(n.type);
					const elemId =
						(typeof n.elementRef === 'string' ? n.elementRef : null) ??
						(canvasOnly ? null : `elem-${id}`);
					const elementsNode = model.elementsNode || model.elements;
					const elements = this._liveList(
						elementsNode,
						'element',
						elementsNode?.element || elementsNode?.elements
					);
					let elem = elemId
						? elements.find(e => (e?.id ?? e?.$attrs?.id) === elemId)
						: null;
					if (!canvasOnly && !elem && elementsNode) {
						const defaultName =
							n.name ??
							this._getDefaultLabelForType(
								this._normalizeType(n.type ?? 'BusinessActor')
							) ??
							n.type ??
							'Element';
						elem = moddle.create('archimate:BaseElement', {
							id: elemId,
							type: n.type ?? 'BusinessActor',
							name: defaultName,
						});
						if (moddle.ids?.claim) moddle.ids.claim(elemId, elem);
						elem.$parent = elementsNode;
						elements.push(elem);
						if (elementsNode.set) elementsNode.set('element', elements);
						else elementsNode.element = elements;
						const newElemId = elem.id ?? elem.$attrs?.id;
						if (this._elementsById && newElemId) this._elementsById[newElemId] = elem;
					}
					const isJunction =
						elem && [JUNCTION, AND_JUNCTION, OR_JUNCTION].includes(elem.type);
					const viewNodeAttrs = {
						id,
						x: n.x ?? 0,
						y: n.y ?? 0,
						w: isJunction
							? 20
							: (n.w ?? (canvasOnly ? STICKER_DEFAULT_SIZE.width : 120)),
						h: isJunction
							? 20
							: (n.h ?? (canvasOnly ? STICKER_DEFAULT_SIZE.height : 60)),
					};
					if (elem) viewNodeAttrs.elementRef = elem;
					if (canvasOnly) viewNodeAttrs.type = n.type;
					const viewNode = moddle.create('archimate:Node', viewNodeAttrs);
					if (n.label != null && String(n.label).trim() !== '') {
						viewNode.label = String(n.label).trim();
					}
					if (n.fillColor || n.lineColor || n.lineWidth != null) {
						const style = viewNode.style || moddle.create('archimate:Style', {});
						if (n.fillColor && typeof n.fillColor === 'object') {
							const fc = n.fillColor;
							style.fillColor = moddle.create('archimate:FillColor', {
								r: fc.r ?? 255,
								g: fc.g ?? 249,
								b: fc.b ?? 177,
								a: fc.a ?? 100,
							});
						}
						if (n.lineColor && typeof n.lineColor === 'object') {
							const lc = n.lineColor;
							style.lineColor = moddle.create('archimate:LineColor', {
								r: lc.r ?? 0,
								g: lc.g ?? 0,
								b: lc.b ?? 0,
								a: lc.a ?? 100,
							});
						}
						if (n.lineWidth != null) style.lineWidth = n.lineWidth;
						viewNode.style = style;
					}
					if (moddle.ids?.claim) moddle.ids.claim(id, viewNode);
					const parentId =
						typeof op.parentNodeId === 'string' && op.parentNodeId.trim() !== ''
							? op.parentNodeId.trim()
							: null;
					const parentViewEl = parentId ? findBy(viewIndex, parentId) : null;
					viewNode.$parent = parentViewEl ?? targetView;
					if (!targetView.viewElements) targetView.viewElements = [];
					const container = parentViewEl ?? targetView;
					if (!container.viewElements) container.viewElements = [];
					const addIdx = container.viewElements.findIndex(
						el => (el?.id ?? el?.$attrs?.id) === id
					);
					if (addIdx >= 0) container.viewElements.splice(addIdx, 1);
					container.viewElements.push(viewNode);
					if (!isOtherView && this._nodeFactory) {
						const type = this._getType(viewNode);
						if (type && this._isRenderableElement(type)) {
							this._applyingPatch = true;
							let cell = null;
							try {
								cell = this.createElementNode(type, {
									id,
									x: viewNode.x ?? 0,
									y: viewNode.y ?? 0,
									width: viewNode.w ?? 120,
									height: viewNode.h ?? 60,
									label: this._getLabel(viewNode),
									viewElement: viewNode,
								});
							} finally {
								this._applyingPatch = false;
							}
							if (parentId && cell) {
								const graph = this.getGraph();
								const parentCell =
									graph?.getCellById?.(parentId) ??
									graph?.model?.getCellById?.(parentId);
								if (parentCell && typeof parentCell.addChild === 'function') {
									this._applyingPatch = true;
									try {
										parentCell.addChild(cell);
										this.bringCellToFront(parentId);
									} finally {
										this._applyingPatch = false;
									}
								}
							}
						}
					}
				} else if (op.op === 'addConnection' && op.connection) {
					const c = op.connection;
					const id = c.identifier ?? `id-conn-${Date.now()}`;
					const elements = getTargetViewElements(viewIndex);
					const nodeMap = {};
					const collectNodes = arr => {
						for (const el of arr) {
							if (!el.waypoints && !el.relationshipRef && !(el.source && el.target)) {
								nodeMap[getElementId(el)] = el;
								if (el.viewElements) collectNodes(el.viewElements);
							}
						}
					};
					collectNodes(elements);
					const srcId = typeof c.source === 'string' ? c.source : getElementId(c.source);
					const tgtId = typeof c.target === 'string' ? c.target : getElementId(c.target);
					const srcNode = srcId ? nodeMap[srcId] : null;
					const tgtNode = tgtId ? nodeMap[tgtId] : null;
					if (!srcNode || !tgtNode) continue;
					if (
						isCanvasOnlyNodeType(this._getType(srcNode)) ||
						isCanvasOnlyNodeType(this._getType(tgtNode))
					) {
						continue;
					}
					const rels =
						model.relationshipsNode?.relationship ||
						model.relationships?.relationship ||
						[];
					const relsArr = Array.isArray(rels) ? rels : [rels].filter(Boolean);
					let relRef = c.relationshipRef
						? relsArr.find(r => (r?.id ?? r?.$attrs?.id) === c.relationshipRef)
						: null;
					if (!relRef && srcNode?.elementRef && tgtNode?.elementRef) {
						let relationshipsNode = model.relationshipsNode || model.relationships;
						if (!relationshipsNode) {
							relationshipsNode = moddle.create(ARCHIMATE_RELATIONSHIPS, {
								relationship: [],
							});
							model.relationshipsNode = relationshipsNode;
							relationshipsNode.$parent = model;
						}
						const relList =
							relationshipsNode.relationship ||
							relationshipsNode.get?.('relationship');
						const relArr = Array.isArray(relList) ? relList : relList ? [relList] : [];
						relRef = moddle.create('archimate:Relationship', {
							type: c.type || RELATIONSHIP_ASSOCIATION,
							source: srcNode.elementRef,
							target: tgtNode.elementRef,
						});
						if (c.relationshipRef) {
							relRef.id = c.relationshipRef;
						} else if (moddle.ids?.nextPrefixed) {
							relRef.id = moddle.ids.nextPrefixed('id-rel-', relRef);
						} else {
							relRef.id = `id-rel-${Date.now()}`;
						}
						if (c.accessType) {
							if (relRef.set) relRef.set('accessType', c.accessType);
							else relRef.accessType = c.accessType;
						}
						if (c.name) {
							if (relRef.set) relRef.set('name', c.name);
							else relRef.name = c.name;
						}
						if (moddle.ids?.claim) moddle.ids.claim(relRef.id, relRef);
						relRef.$parent = relationshipsNode;
						relArr.push(relRef);
						if (relationshipsNode.set) relationshipsNode.set('relationship', relArr);
						else relationshipsNode.relationship = relArr;
					}
					const connObj = moddle.create('archimate:Connection', {id});
					if (moddle.ids?.claim) moddle.ids.claim(id, connObj);
					connObj.source = srcNode;
					connObj.target = tgtNode;
					if (relRef) connObj.relationshipRef = relRef;
					connObj.$parent = targetView;
					if (c.bendpoints?.length) {
						const waypointEls = c.bendpoints.map(p =>
							moddle.create('archimate:Waypoint', {x: p.x, y: p.y})
						);
						const wp = moddle.create('archimate:Waypoints', {waypoints: waypointEls});
						if (wp.waypoints) {
							for (const w of wp.waypoints) {
								if (w) w.$parent = wp;
							}
						}
						wp.$parent = connObj;
						connObj.waypointsNode = wp;
					}
					targetView.viewElements.push(connObj);
					// Добавляем только новое ребро на граф, без полной перерисовки (openView создаёт дубликаты всех узлов)
					if (!isOtherView && this._edgeFactory) {
						const lineColor =
							c.lineColor && typeof c.lineColor === 'object'
								? toRgbaString(c.lineColor)
								: '#000000';
						this._applyingPatch = true;
						try {
							this._edgeFactory.createAndAdd({
								id,
								source: srcId,
								target: tgtId,
								vertices: Array.isArray(c.bendpoints) ? c.bendpoints : [],
								relationshipType: c.type || RELATIONSHIP_ASSOCIATION,
								accessType: c.accessType,
								lineColor,
								label: c.name ?? '',
							});
						} finally {
							this._applyingPatch = false;
						}
					} else if (isOtherView) {
						needsRerender = true;
					}
				}
			}
		}

		if (needsRerender && this._currentView) {
			const viewId = this._currentView.id ?? this._currentView.$attrs?.id;
			if (viewId) this.openView(viewId);
		} else {
			applyViewpointDimming(this);
		}
	}

	saveXML(options = {}) {
		const model = this._model;
		if (!model) {
			return Promise.reject(new Error('no model loaded'));
		}
		return this._moddle.toXML(model, options).then(result => {
			const xml = prepareForExport(result?.xml ?? '');
			return {xml};
		});
	}

	saveJSON() {
		if (!this._model) {
			return Promise.reject(new Error('no model loaded'));
		}
		const json = modelToJson(this._model);
		return Promise.resolve({json});
	}

	_resolveView(viewOrId) {
		return renderView.resolveView(this, viewOrId);
	}

	_renderView(view) {
		renderView.renderView(this, view);
		applyViewpointDimming(this);
	}

	_collectAllNodeViewElements(view, parentViewElement = null) {
		return renderView.collectAllNodeViewElements(view, parentViewElement);
	}

	_collectAllConnectionViewElements(view) {
		return renderView.collectAllConnectionViewElements(this, view);
	}

	_isConnectionViewElement(el) {
		return renderView.isConnectionViewElement(el);
	}

	_getConnectionEndpointId(sourceOrTarget) {
		return renderView.getConnectionEndpointId(sourceOrTarget);
	}

	_getConnectionWaypoints(connectionViewElement) {
		return renderView.getConnectionWaypoints(connectionViewElement);
	}

	_getRelationshipType(connectionViewElement) {
		return renderView.getRelationshipType(this, connectionViewElement);
	}

	_getRelationshipAccessType(connectionViewElement) {
		return renderView.getRelationshipAccessType(this, connectionViewElement);
	}

	addConnectionEdge(opts) {
		return this._edgeFactory.createAndAdd(opts);
	}

	_getEdgeMarkerConfigForRelationshipType(relationshipType, options = {}) {
		return renderView.getEdgeMarkerConfigForRelationshipType(relationshipType, options);
	}

	_getBounds(viewElement) {
		return viewHelpers.getBounds(this, viewElement);
	}

	_getLabel(viewElement) {
		return viewHelpers.getLabel(this, viewElement);
	}

	_getDefaultLabelForType(normalizedType) {
		return viewHelpers.getDefaultLabelForType(this, normalizedType);
	}

	getDefaultLabelForType(type) {
		return viewHelpers.getDefaultLabelForType(this, viewHelpers.normalizeType(type));
	}

	_getType(viewElement) {
		return viewHelpers.getType(this, viewElement);
	}

	_getElementRef(viewElement) {
		return viewHelpers.getElementRef(this, viewElement);
	}

	_getAttrCaseInsensitive(attrs, name) {
		return viewHelpers.getAttrCaseInsensitive(attrs, name);
	}

	_collectElementsByIdentifier(model) {
		return viewHelpers.collectElementsByIdentifier(model);
	}

	_normalizeType(type) {
		return viewHelpers.normalizeType(type);
	}

	_isApplicationComponent(type) {
		return viewHelpers.isApplicationComponent(this, type);
	}

	_isRenderableElement(type) {
		return viewHelpers.isRenderableElement(this, type);
	}

	_buildElementNodeConfig(type, opts = {}) {
		return nodeConfig.buildElementNodeConfig(this, type, opts);
	}

	_toKebabCase(value) {
		return nodeConfig.toKebabCase(value);
	}

	static ICON_BOX_SIZE = nodeConfig.ICON_BOX_SIZE;

	_getIconPath(iconKey, width, height) {
		return nodeConfig.getIconPath(this, iconKey, width, height);
	}

	_prepareForImport(xml) {
		return importPrep.prepareForImport(xml);
	}
}
